/**
 * ProcurementAccountsView.jsx — v16.2 (Professional · Monochrome · Plain Tables · ON HOLD · PRINT · BULK · STATUS EXPORT)
 * =====================================================================================
 * v16.2 CHANGES — "Export Excel by status":
 *  - Batch Management tab: new "Export Invoices by Status" bar — pick Status
 *    (All / Pending / On Hold / Approved / Rejected), Category and Batch scope
 *    (All / Received only / Open only) → Export Excel downloads an .xlsx of every
 *    matching invoice across ALL batches (e.g. all Rejected invoices).
 *  - Batch drawer: new Status filter (works together with the Category filter) and an
 *    "Export Filtered" button → .xlsx of exactly the rows you see (e.g. Batch 156 Rejected).
 *  - Workbook: "Invoices" sheet (title, filters, autofilter, number formats, totals row)
 *    + "By Vendor" summary sheet (count, subtotal, VAT, total per vendor).
 *  - Built client-side with SheetJS (`xlsx` package) from /api/procurement/submissions,
 *    paged 500 at a time. No backend change.
 *
 * v16.1: Bulk flip Approved ⇄ Rejected (approved / rejected rows selectable).
 * v16: Bulk approve / bulk reject with per-invoice remarks (3 concurrent requests).
 * v15.1: Print invoice sheet (A4).
 * v15: "On Hold" status (park an invoice, live preview of uploader message, On Hold tab).
 * v14: PLAIN mode (yellow core columns, +/- extra column toggle, AED split).
 * v13: Accounts status override from detail drawer.
 * v12: Received-batch-only stats; Dashboard aesthetic.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Tag, Button, Table, Spin, Empty, Input, Select,
  Space, Divider, Tabs, Modal, Drawer, Descriptions,
  Alert, message, Badge, Popconfirm, Tooltip,
} from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  LockOutlined, UnlockOutlined, NumberOutlined, EyeOutlined,
  SearchOutlined, ReloadOutlined, SyncOutlined, DollarOutlined,
  FileTextOutlined, FolderOpenOutlined, DownloadOutlined,
  LeftOutlined, RightOutlined,
  FileExcelOutlined, FilePdfOutlined, LoadingOutlined,
  EditOutlined, CheckOutlined, CloseOutlined, SaveOutlined,
  SettingOutlined, FolderAddOutlined, LinkOutlined,
  DisconnectOutlined, InboxOutlined,
  PauseCircleOutlined, PlayCircleOutlined, PrinterOutlined,
  CheckSquareOutlined, FilterOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import BulkPdfDownloadButtons from './BulkPdfDownloadButtons';
import BatchPdfDownloadDropdown from './BatchPdfDownloadDropdown';

const { Text } = Typography;
const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const FONT_FAMILY = "'Segoe UI', Calibri, 'Helvetica Neue', sans-serif";

/* ═══════════════════════════════════════════
   STYLE TOKENS
   ═══════════════════════════════════════════ */
const S = {
  page:          { fontFamily: FONT_FAMILY, fontSize: 13, color: '#1a1a1a' },
  sectionTitle:  { fontSize: 12, fontWeight: 700, color: '#1a1a1a', letterSpacing: 0.3 },
  subLabel:      { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 },
  panel:         { background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, marginBottom: 14 },
  panelHeader:   { padding: '8px 14px', borderBottom: '1px solid #e8e8e8', background: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  panelBody:     { padding: '12px 14px' },
  plainBtn:      { borderRadius: 3, fontSize: 11 },
  holdBtn:       { borderRadius: 3, fontSize: 11, color: '#d46b08', borderColor: '#ffd591' },

  statRow:       { background: '#fff', border: '1px solid #d9d9d9', borderRadius: 3, display: 'flex', flexWrap: 'wrap', marginBottom: 14 },
  statCell:      (last) => ({ flex: '1 1 0', minWidth: 130, padding: '10px 14px', borderRight: last ? 'none' : '1px solid #e8e8e8' }),
  statLabel:     { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2, fontWeight: 600 },
  statValue:     (c) => ({ fontSize: 17, fontWeight: 700, color: c || '#1a1a1a', lineHeight: 1.2 }),
  statSub:       { fontSize: 10, color: '#aaa', marginTop: 2 },
};

/* ─── Constants ─── */
const CATEGORY_LABELS = {
  material: 'Materials', labor_supply: 'Labor Supply', sub_contractor: 'Sub-Contractors',
  heavy_equipment_rental: 'Heavy Equipment Rental', equipment_rental: 'Equipment Rental',
  fees_and_charges: 'Fees & Charges', repairs_and_maintenance: 'Repairs & Maintenance',
};
const CATEGORY_COLORS = {
  material: '#1a5276', labor_supply: '#531dab', sub_contractor: '#08979c',
  heavy_equipment_rental: '#c41d7f', equipment_rental: '#d4380d',
  fees_and_charges: '#237804', repairs_and_maintenance: '#b38600',
};
const HOLD_COLOR = '#d46b08';
const STATUS_CFG = {
  pending_review: { color: 'gold', icon: <ClockCircleOutlined />, label: 'Pending' },
  under_review: { color: 'processing', icon: <SyncOutlined spin />, label: 'Under Review' },
  on_hold: { color: 'orange', icon: <PauseCircleOutlined />, label: 'On Hold' },
  approved: { color: 'success', icon: <CheckCircleOutlined />, label: 'Approved' },
  rejected: { color: 'error', icon: <CloseCircleOutlined />, label: 'Rejected' },
};
const DOC_TYPE_LABELS = {
  invoice: 'Invoice', delivery_note: 'Delivery Note', timesheet: 'Timesheet',
  combined_pdf: 'Combined PDF', revised_combined_pdf: 'Revised PDF', attachment: 'Attachment',
};
const AUDIT_LABELS = {
  submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected',
  edited: 'Edited', deleted: 'Deleted', revision_submitted: 'Revision Submitted',
  status_changed: 'Status Changed', held: 'Put On Hold', hold_released: 'Hold Released',
};

/* v16 — bulk review */
const REVIEWABLE_STATUSES = ['pending_review', 'under_review', 'on_hold'];
const isReviewable = (r) => REVIEWABLE_STATUSES.includes(r?.status);
const BULK_CONCURRENCY = 3;
const QUICK_REJECT_REASONS = [
  'TRN mismatch',
  'LPO number not matching',
  'Amount does not match LPO',
  'Delivery note missing / unsigned',
  'Duplicate invoice',
  'VAT calculation incorrect',
  'Invoice not addressed to Phoenician',
];

/* v16.1 — approved / rejected rows can also be ticked to flip their status */
const SELECTABLE_STATUSES = [...REVIEWABLE_STATUSES, 'approved', 'rejected'];
const isSelectable = (r) => SELECTABLE_STATUSES.includes(r?.status);
const isDecided = (r) => r?.status === 'approved' || r?.status === 'rejected';

/* v16.2 — status filter options (Pending covers pending_review + under_review) */
const STATUS_FILTER_OPTIONS = [
  { value: 'all',      label: 'All Statuses' },
  { value: 'pending',  label: 'Pending' },
  { value: 'on_hold',  label: 'On Hold' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];
const STATUS_FILTER_LABEL = Object.fromEntries(STATUS_FILTER_OPTIONS.map(o => [o.value, o.label]));
const matchesStatusFilter = (status, filter) => {
  if (filter === 'all') return true;
  if (filter === 'pending') return status === 'pending_review' || status === 'under_review';
  return status === filter;
};
/* API status values to query for a given filter */
const apiStatusesFor = (filter) => {
  if (filter === 'all') return [null];
  if (filter === 'pending') return ['pending_review', 'under_review'];
  return [filter];
};
const EXPORT_PAGE_SIZE = 500;

const fmt = (val) => { if (!val && val !== 0) return '-'; return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const fmtNum = (v) => (v || v === 0) ? Number(v).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtDate = (val) => { if (!val) return '-'; return new Date(val).toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric' }); };
const fmtDateTime = (val) => { if (!val) return '-'; return new Date(val).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }); };
const statusTag = (s) => { const c = STATUS_CFG[s] || { color: 'default', label: s || '-' }; return <Tag icon={c.icon} color={c.color} style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px' }}>{c.label}</Tag>; };
const isFileSystemAccessSupported = () => 'showDirectoryPicker' in window;
const todayDubai = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }); // YYYY-MM-DD
const safeFilePart = (s) => String(s || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/* Mirrors build_hold_message() in procurement_db.py so the preview matches
   exactly what the API will store and what the uploader will read. */
const buildHoldMessage = (vendorName, invoiceNumber, submissionRef, note) => {
  const vendor = (vendorName || '').trim() || 'Unknown supplier';
  const inv = (invoiceNumber || '').trim() || (submissionRef || '').trim() || '—';
  const base = `Invoice ${inv} from ${vendor} is currently ON HOLD with Accounts and is under review.`;
  const n = (note || '').trim();
  return n ? `${base} Note: ${n}` : base;
};

/* ═══════════════════════════════════════════
   v16.2 — EXCEL EXPORT (SheetJS)
   Sheet 1 "Invoices": title, filter line, header, rows, totals.
   Sheet 2 "By Vendor": count / subtotal / VAT / total per vendor.
   ═══════════════════════════════════════════ */
const toNum = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? null : Number(v);

const exportInvoicesXlsx = (rows, { title, filterLine, filename, batchStatusMap = {} }) => {
  const header = [
    '#', 'Batch', 'Batch Status', 'Category', 'LPO', 'Vendor', 'Vendor TRN',
    'Invoice No.', 'Invoice Date', 'Project Code', 'DN No.', 'DN Date',
    'Subtotal (AED)', 'VAT (AED)', 'Total (AED)', 'Status', 'Accounts Remarks',
    'Rev', 'Submitted By', 'Submitted On', 'Ref',
  ];
  const AMT_COLS = [12, 13, 14];

  let sumSub = 0, sumVat = 0, sumTotal = 0;
  const body = rows.map((r, i) => {
    const total = toNum(r.total_amount) ?? 0;
    const vat = toNum(r.vat_amount) ?? 0;
    const sub = toNum(r.subtotal_amount) ?? (total - vat);
    sumSub += sub; sumVat += vat; sumTotal += total;
    const bStatus = r.batch_no ? ((batchStatusMap[r.batch_no] || 'open') === 'received' ? 'Received' : 'Open') : '';
    return [
      i + 1,
      r.batch_no ?? '',
      bStatus,
      CATEGORY_LABELS[r.category] || r.category || '',
      r.lpo_number || '',
      r.vendor_name || '',
      r.vendor_trn || '',
      r.invoice_number || '',
      r.invoice_date || '',
      r.project_code || '',
      r.dn_number || '',
      r.dn_date || '',
      sub,
      vat,
      total,
      STATUS_CFG[r.status]?.label || r.status || '',
      r.status === 'on_hold' ? (r.hold_remarks || r.accounts_remarks || '') : (r.accounts_remarks || ''),
      r.revision_count || 0,
      (r.submitted_by || '').split('@')[0],
      r.submitted_at ? fmtDate(r.submitted_at) : '',
      r.submission_ref || '',
    ];
  });

  const totalsRow = new Array(header.length).fill('');
  totalsRow[11] = `TOTAL (${rows.length} invoices)`;
  totalsRow[12] = sumSub; totalsRow[13] = sumVat; totalsRow[14] = sumTotal;

  const HEADER_ROW = 3; // 0-based row index of the header
  const aoa = [
    [title],
    [`${filterLine} · Generated ${fmtDateTime(new Date())}`],
    [],
    header,
    ...body,
    [],
    totalsRow,
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = [
    { wch: 5 }, { wch: 7 }, { wch: 10 }, { wch: 16 }, { wch: 8 }, { wch: 38 }, { wch: 17 },
    { wch: 15 }, { wch: 12 }, { wch: 11 }, { wch: 11 }, { wch: 11 },
    { wch: 15 }, { wch: 13 }, { wch: 15 }, { wch: 12 }, { wch: 45 },
    { wch: 5 }, { wch: 14 }, { wch: 12 }, { wch: 18 },
  ];
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } },
  ];
  if (body.length) {
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: HEADER_ROW, c: 0 }, e: { r: HEADER_ROW + body.length, c: header.length - 1 } }) };
  }
  // number format on amount columns (body + totals)
  const lastRow = aoa.length - 1;
  for (let r = HEADER_ROW + 1; r <= lastRow; r++) {
    AMT_COLS.forEach(c => {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === 'n') cell.z = '#,##0.00';
    });
  }
  ws['!freeze'] = { xSplit: 0, ySplit: HEADER_ROW + 1 };

  /* By Vendor summary */
  const vMap = {};
  rows.forEach(r => {
    const k = (r.vendor_name || 'Unknown').trim();
    if (!vMap[k]) vMap[k] = { n: 0, sub: 0, vat: 0, total: 0 };
    const total = toNum(r.total_amount) ?? 0;
    const vat = toNum(r.vat_amount) ?? 0;
    vMap[k].n++; vMap[k].vat += vat; vMap[k].total += total;
    vMap[k].sub += toNum(r.subtotal_amount) ?? (total - vat);
  });
  const vRows = Object.entries(vMap).sort((a, b) => b[1].total - a[1].total)
    .map(([v, s]) => [v, s.n, s.sub, s.vat, s.total]);
  const vAoa = [
    [`${title} — By Vendor`],
    [filterLine],
    [],
    ['Vendor', 'Invoices', 'Subtotal (AED)', 'VAT (AED)', 'Total (AED)'],
    ...vRows,
    [],
    ['TOTAL', rows.length, sumSub, sumVat, sumTotal],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(vAoa);
  ws2['!cols'] = [{ wch: 42 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 16 }];
  for (let r = 4; r < vAoa.length; r++) {
    [2, 3, 4].forEach(c => {
      const cell = ws2[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === 'n') cell.z = '#,##0.00';
    });
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
  XLSX.utils.book_append_sheet(wb, ws2, 'By Vendor');
  XLSX.writeFile(wb, filename);
};

/* ═══════════════════════════════════════════
   v15.1 — PRINTABLE A4 INVOICE-REVIEW SHEET
   Builds a clean, self-contained HTML doc from the invoice detail data only.
   Supplier name bold at the top, invoice number + amount, status + remarks,
   then full invoice details. No print/audit dates (only the invoice date).
   ═══════════════════════════════════════════ */
const printEsc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const buildPrintInvoiceHtml = (d) => {
  const statusColorMap = {
    pending_review: '#d46b08', under_review: '#1a5276',
    on_hold: HOLD_COLOR, approved: '#237804', rejected: '#cf1322',
  };
  const statusLabel = STATUS_CFG[d.status]?.label || d.status || '-';
  const statusColor = statusColorMap[d.status] || '#555';
  const isHold = d.status === 'on_hold';
  const remarks = isHold ? (d.hold_remarks || d.accounts_remarks) : d.accounts_remarks;
  const actor = isHold ? d.held_by : d.reviewed_by;

  const row = (label, value) => (value || value === 0)
    ? `<tr><td class="lbl">${printEsc(label)}</td><td>${printEsc(value)}</td></tr>` : '';

  const detailRows = [
    row('Batch', d.batch_no),
    row('Category', CATEGORY_LABELS[d.category] || d.category),
    row('LPO Number', d.lpo_number),
    row('Invoice No.', d.invoice_number),
    row('Invoice Date', d.invoice_date),
    row('Project Code', d.project_code),
    row('Vendor TRN', d.vendor_trn),
    row('Vendor Email', d.vendor_email),
    row('Vendor Phone', d.vendor_phone),
    row('Description', d.description),
  ].join('');

  const hasDN = d.dn_number || d.dn_date || d.dn_received_by || d.dn_project_code;
  const dnRows = hasDN ? [
    row('DN Number', d.dn_number),
    row('DN Date', d.dn_date),
    row('Received By', d.dn_received_by),
    row('DN Project', d.dn_project_code),
  ].join('') : '';

  const docs = d.documents || [];
  const docRows = docs.length
    ? docs.map(doc => `<tr><td>${printEsc(DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type)}</td><td>${printEsc(doc.renamed_filename || doc.original_filename || '')}</td></tr>`).join('')
    : '';

  const statusMeta = actor
    ? `<span class="status-meta">by ${printEsc((actor || '').split('@')[0])}</span>` : '';

  /* Full remarks history — every recorded action/remark, shown even when the
     invoice is already Approved. Sourced from the audit trail plus any per-
     revision rejection remarks so nothing historical is dropped. */
  const auditLogs = d.audit_logs || [];
  const revs = d.revisions || [];
  const histEntries = [];
  auditLogs.forEach((log) => {
    const det = log.details || {};
    const transition = (det.from && det.to)
      ? `${STATUS_CFG[det.from]?.label || det.from} → ${STATUS_CFG[det.to]?.label || det.to}` : '';
    const note = det.remarks || det.manual_note || '';
    const text = [note, transition].filter(Boolean).join(' · ');
    histEntries.push({
      action: AUDIT_LABELS[log.action] || log.action || '-',
      key: log.action,
      by: (log.performed_by || '').split('@')[0] || '',
      text: text || '—',
    });
  });
  revs.forEach((r) => {
    if ((r.accounts_remarks || '').trim()) {
      histEntries.push({
        action: `Rev ${r.revision_number} · ${STATUS_CFG[r.status]?.label || r.status || ''}`.trim(),
        key: r.status,
        by: '',
        text: r.accounts_remarks,
      });
    }
  });
  const histRows = histEntries.map((e) => {
    const ac = e.key === 'rejected' ? '#cf1322'
      : e.key === 'approved' ? '#237804'
      : e.key === 'held' ? HOLD_COLOR
      : '#1a1a1a';
    return `<tr><td class="act" style="color:${ac}">${printEsc(e.action)}</td><td class="by">${printEsc(e.by || '—')}</td><td class="rmk">${printEsc(e.text)}</td></tr>`;
  }).join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
<title>${printEsc(d.invoice_number || d.submission_ref || 'Invoice')}</title>
<style>
  @page { size: A4 portrait; margin: 14mm 15mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Calibri, 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; font-size: 12px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1a1a1a; padding-bottom: 8px; margin-bottom: 16px; }
  .head .title { font-size: 11px; letter-spacing: 1.2px; text-transform: uppercase; color: #888; font-weight: 700; }
  .head .company { font-size: 14px; font-weight: 700; margin-top: 2px; }
  .head .ref { font-size: 11px; color: #555; text-align: right; line-height: 1.6; }
  .head .ref b { color: #1a1a1a; }
  .vendor-name { font-size: 23px; font-weight: 800; text-transform: uppercase; letter-spacing: .3px; line-height: 1.15; margin-bottom: 10px; }
  .keymeta { display: flex; gap: 40px; flex-wrap: wrap; margin-bottom: 14px; }
  .keymeta .k { font-size: 9px; text-transform: uppercase; letter-spacing: .5px; color: #888; font-weight: 700; margin-bottom: 2px; }
  .keymeta .v { font-size: 16px; font-weight: 700; }
  .keymeta .amount .v { color: #1a5276; }
  .status-bar { padding: 10px 13px; border: 1px solid #d9d9d9; border-left: 4px solid ${statusColor}; border-radius: 3px; margin-bottom: 16px; background: #fafafa; }
  .status-top { display: flex; align-items: center; gap: 10px; }
  .status-pill { font-size: 15px; font-weight: 800; text-transform: uppercase; color: ${statusColor}; white-space: nowrap; }
  .status-meta { margin-left: auto; font-size: 11px; color: #555; text-align: right; white-space: nowrap; }
  .status-remarks { font-size: 12px; color: #1a1a1a; margin-top: 7px; line-height: 1.5; }
  .status-remarks .rk { font-size: 9px; text-transform: uppercase; letter-spacing: .5px; color: #888; font-weight: 700; margin-right: 5px; }
  .status-remarks .none { color: #aaa; }
  h4.sec { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #888; font-weight: 700; margin: 16px 0 6px; }
  table.details { width: 100%; border-collapse: collapse; }
  table.details td { border: 1px solid #e2e2e2; padding: 5px 10px; font-size: 11px; vertical-align: top; }
  table.details td.lbl { background: #fafafa; color: #555; font-weight: 600; width: 135px; white-space: nowrap; }
  .totals { margin: 14px 0 0 auto; width: 290px; border: 1px solid #d9d9d9; border-radius: 3px; overflow: hidden; }
  .totals .r { display: flex; justify-content: space-between; padding: 6px 13px; font-size: 12px; }
  .totals .r.grand { border-top: 1px solid #d9d9d9; font-weight: 800; font-size: 14px; background: #fafafa; }
  .totals .r .amt { font-variant-numeric: tabular-nums; }
  table.docs { width: 100%; border-collapse: collapse; }
  table.docs td { border-bottom: 1px solid #eee; padding: 4px 8px; font-size: 10px; color: #555; }
  table.docs td:first-child { width: 150px; color: #333; }
  table.hist { width: 100%; border-collapse: collapse; }
  table.hist thead th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: .5px; color: #888; font-weight: 700; padding: 4px 10px; border-bottom: 1.5px solid #d9d9d9; }
  table.hist td { border-bottom: 1px solid #eee; padding: 5px 10px; font-size: 11px; vertical-align: top; }
  table.hist td.act { font-weight: 700; white-space: nowrap; width: 120px; }
  table.hist td.by { color: #555; white-space: nowrap; width: 90px; }
  table.hist td.rmk { color: #1a1a1a; line-height: 1.45; }
  table.hist tr:last-child td { border-bottom: none; }
  .foot { margin-top: 22px; padding-top: 8px; border-top: 1px solid #e2e2e2; display: flex; justify-content: space-between; font-size: 10px; color: #444; }
  .foot b { color: #1a1a1a; font-weight: 700; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="head">
    <div>
      <div class="title">Invoice Review Sheet</div>
      <div class="company">Phoenician Technical Services</div>
    </div>
    <div class="ref">
      Ref: <b>${printEsc(d.submission_ref || '-')}</b><br>
      ${d.batch_no ? 'Batch: <b>' + printEsc(d.batch_no) + '</b>' : ''}
    </div>
  </div>

  <div class="vendor-name">${printEsc(d.vendor_name || 'Unknown Supplier')}</div>

  <div class="keymeta">
    <div><div class="k">Invoice No.</div><div class="v">${printEsc(d.invoice_number || '—')}</div></div>
    <div class="amount"><div class="k">Total Amount</div><div class="v">${printEsc(fmt(d.total_amount))}</div></div>
  </div>

  <div class="status-bar">
    <div class="status-top">
      <span class="status-pill">${printEsc(statusLabel)}</span>
      ${statusMeta}
    </div>
    <div class="status-remarks">
      <span class="rk">Remarks</span>${remarks ? printEsc(remarks) : '<span class="none">None recorded</span>'}
    </div>
  </div>

  <h4 class="sec">Invoice Details</h4>
  <table class="details"><tbody>${detailRows}</tbody></table>

  <div class="totals">
    <div class="r"><span>Subtotal</span><span class="amt">${printEsc(fmt(d.subtotal_amount))}</span></div>
    <div class="r"><span>VAT (${printEsc(d.vat_percentage || 5)}%)</span><span class="amt">${printEsc(fmt(d.vat_amount))}</span></div>
    <div class="r grand"><span>Total</span><span class="amt">${printEsc(fmt(d.total_amount))}</span></div>
  </div>

  ${hasDN ? '<h4 class="sec">Delivery Note</h4><table class="details"><tbody>' + dnRows + '</tbody></table>' : ''}

  ${docs.length ? '<h4 class="sec">Documents (' + docs.length + ')</h4><table class="docs"><tbody>' + docRows + '</tbody></table>' : ''}

  ${histEntries.length ? '<h4 class="sec">Remarks History</h4><table class="hist"><thead><tr><th>Action</th><th>By</th><th>Remark / Change</th></tr></thead><tbody>' + histRows + '</tbody></table>' : ''}

  <div class="foot">
    <span>Submitted by <b>${printEsc((d.submitted_by || '').split('@')[0] || '—')}</b></span>
    <span>${printEsc(d.category ? (CATEGORY_LABELS[d.category] || d.category) : '')}</span>
  </div>

  <script>window.addEventListener('load',function(){setTimeout(function(){window.focus();window.print();},250);});</script>
</body></html>`;
};

const ProcurementAccountsView = ({ username }) => {
  const [activeMainTab, setActiveMainTab] = useState('batches');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalSearched, setGlobalSearched] = useState(false);
  const [batchSummaries, setBatchSummaries] = useState([]);
  const [batchLoading, setBatchLoading] = useState(true);
  const [batchSearch, setBatchSearch] = useState('');
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [allTotal, setAllTotal] = useState(0);
  const [allLoading, setAllLoading] = useState(false);
  const [allPage, setAllPage] = useState(1);
  const [allPageSize, setAllPageSize] = useState(25);
  const [allStatusFilter, setAllStatusFilter] = useState('all');
  const [allCategoryFilter, setAllCategoryFilter] = useState('all');
  const [allSearchText, setAllSearchText] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchSubmissions, setBatchSubmissions] = useState([]);
  const [batchInfo, setBatchInfo] = useState(null);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);
  const [batchCatFilter, setBatchCatFilter] = useState('all');
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');   // v16.2
  const [showExtraCols, setShowExtraCols] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [statusRemarks, setStatusRemarks] = useState('');
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  /* v15 — hold */
  const [holdRemarks, setHoldRemarks] = useState('');
  const [holdLoading, setHoldLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDocs, setPreviewDocs] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeDocIdx, setActiveDocIdx] = useState(0);
  const [downloadingExcel, setDownloadingExcel] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [downloadingAllExcel, setDownloadingAllExcel] = useState(false);
  const [savingDocId, setSavingDocId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  const [editFieldSaving, setEditFieldSaving] = useState(false);

  /* v16 — bulk selection & bulk review */
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});     // id -> row snapshot (survives pagination)
  const [bulkMode, setBulkMode] = useState(null);         // 'approved' | 'rejected' | null
  const [bulkRemarks, setBulkRemarks] = useState({});     // id -> remark text
  const [bulkCommon, setBulkCommon] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState({});     // id -> { ok, error }

  /* v16.2 — status export (Batch Management) */
  const [exportStatus, setExportStatus] = useState('rejected');
  const [exportCategory, setExportCategory] = useState('all');
  const [exportScope, setExportScope] = useState('all');      // 'all' | 'received' | 'open'
  const [exportingStatus, setExportingStatus] = useState(false);

  /* ═══════════════════════════════════════════
     FOLDER PATH — File System Access API
     ═══════════════════════════════════════════ */
  const [rootDirHandle, setRootDirHandle] = useState(null);
  const [rootDirName, setRootDirName] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('procurement_download_folder_name');
    if (saved) setRootDirName(saved);
  }, []);

  const pickDownloadFolder = async () => {
    if (!isFileSystemAccessSupported()) { message.error('Your browser does not support folder selection. Use Chrome or Edge.'); return; }
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
      setRootDirHandle(handle);
      setRootDirName(handle.name);
      localStorage.setItem('procurement_download_folder_name', handle.name);
      message.success(`Download folder set: ${handle.name}`);
    } catch (err) { if (err.name !== 'AbortError') message.error('Failed to select folder'); }
  };

  const disconnectFolder = () => {
    setRootDirHandle(null); setRootDirName('');
    localStorage.removeItem('procurement_download_folder_name');
    message.info('Download folder disconnected.');
  };

  const saveToLinkedFolder = async (streamUrl, filename, batchNo) => {
    if (!rootDirHandle) {
      const a = document.createElement('a');
      a.href = streamUrl; a.download = filename; a.style.display = 'none';
      document.body.appendChild(a); a.click();
      setTimeout(() => document.body.removeChild(a), 500);
      return { method: 'browser' };
    }
    try {
      let perm = await rootDirHandle.queryPermission({ mode: 'readwrite' });
      if (perm !== 'granted') {
        perm = await rootDirHandle.requestPermission({ mode: 'readwrite' });
        if (perm !== 'granted') {
          message.warning('Folder permission denied. Please re-link your folder.');
          setRootDirHandle(null); setRootDirName('');
          const a = document.createElement('a'); a.href = streamUrl; a.download = filename;
          document.body.appendChild(a); a.click(); setTimeout(() => document.body.removeChild(a), 500);
          return { method: 'browser' };
        }
      }
      const response = await fetch(streamUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const batchFolderName = batchNo ? `Batch-${batchNo}` : 'No-Batch';
      const batchDirHandle = await rootDirHandle.getDirectoryHandle(batchFolderName, { create: true });
      const fileHandle = await batchDirHandle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { method: 'folder', path: `${batchFolderName}/${filename}` };
    } catch (err) {
      console.error('File System Access write error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
        message.warning('Folder access expired. Please re-link.');
        setRootDirHandle(null);
      }
      const a = document.createElement('a'); a.href = streamUrl; a.download = filename;
      document.body.appendChild(a); a.click(); setTimeout(() => document.body.removeChild(a), 500);
      return { method: 'browser_fallback', error: err.message };
    }
  };

  /* ═══════════════════════════════════════════
     v16 — SELECTION HELPERS
     ═══════════════════════════════════════════ */
  const clearSelection = () => { setSelectedIds([]); setSelectedMap({}); };

  const removeFromSelection = (ids) => {
    const drop = new Set(Array.isArray(ids) ? ids : [ids]);
    setSelectedIds(prev => prev.filter(x => !drop.has(x)));
    setSelectedMap(prev => { const n = { ...prev }; drop.forEach(id => delete n[id]); return n; });
    setBulkRemarks(prev => { const n = { ...prev }; drop.forEach(id => delete n[id]); return n; });
  };

  const rowSelection = {
    selectedRowKeys: selectedIds,
    preserveSelectedRowKeys: true,
    columnWidth: 34,
    fixed: 'left',
    onChange: (keys, rows) => {
      setSelectedIds(keys);
      setSelectedMap(prev => {
        const next = {};
        keys.forEach(k => {
          const r = (rows || []).find(x => x && x.id === k) || prev[k];
          if (r) next[k] = r;
        });
        return next;
      });
    },
    /* v16.1 — approved / rejected rows are selectable so they can be flipped */
    getCheckboxProps: (r) => ({
      disabled: !isSelectable(r),
      title: !isSelectable(r) ? `Cannot change — ${STATUS_CFG[r.status]?.label || r.status}` : undefined,
    }),
    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
  };

  const selectedRows = useMemo(
    () => selectedIds.map(id => selectedMap[id]).filter(Boolean),
    [selectedIds, selectedMap],
  );
  const selectedTotal = useMemo(
    () => selectedRows.reduce((s, r) => s + (Number(r.total_amount) || 0), 0),
    [selectedRows],
  );

  /* v16.1 — rows the current bulk action will actually change (skip rows already in the target status) */
  const bulkActionRows = useMemo(
    () => (bulkMode ? selectedRows.filter(r => r.status !== bulkMode) : selectedRows),
    [selectedRows, bulkMode],
  );
  const bulkSkipRows = useMemo(
    () => (bulkMode ? selectedRows.filter(r => r.status === bulkMode) : []),
    [selectedRows, bulkMode],
  );
  const bulkActionTotal = useMemo(
    () => bulkActionRows.reduce((s, r) => s + (Number(r.total_amount) || 0), 0),
    [bulkActionRows],
  );

  /* ═══════════════════════════════════════════
     DATA FETCHING
     ═══════════════════════════════════════════ */
  const fetchStats = useCallback(async () => { setStatsLoading(true); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/dashboard/stats`); setStats(res.data.stats || {}); } catch {} finally { setStatsLoading(false); } }, []);
  const fetchBatchSummaries = useCallback(async () => { setBatchLoading(true); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/summary`); if (res.data.success) setBatchSummaries(res.data.summaries || []); } catch {} finally { setBatchLoading(false); } }, []);
  const fetchAllSubmissions = useCallback(async () => { setAllLoading(true); try { const params = { limit: allPageSize, offset: (allPage - 1) * allPageSize }; if (allStatusFilter !== 'all') params.status = allStatusFilter; if (allCategoryFilter !== 'all') params.category = allCategoryFilter; const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, { params }); setAllSubmissions(res.data.submissions || []); setAllTotal(res.data.total || 0); } catch {} finally { setAllLoading(false); } }, [allPage, allPageSize, allStatusFilter, allCategoryFilter]);
  useEffect(() => { fetchStats(); fetchBatchSummaries(); }, [fetchStats, fetchBatchSummaries]);
  useEffect(() => { if (['all', 'pending', 'hold'].includes(activeMainTab)) fetchAllSubmissions(); }, [activeMainTab, fetchAllSubmissions]);

  useEffect(() => {
  if (!globalQuery || globalQuery.trim().length < 2) { setGlobalResults([]); setGlobalSearched(false); return; }
  const timer = setTimeout(async () => {
    setGlobalSearching(true);
    setGlobalSearched(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, {
        params: { search: globalQuery.trim(), limit: 500 },
      });
      setGlobalResults(res.data.submissions || []);
    } catch { setGlobalResults([]); }
    finally { setGlobalSearching(false); }
  }, 400);
  return () => clearTimeout(timer);
}, [globalQuery]);

  const openBatch = async (batchNo) => { setSelectedBatch(batchNo); setBatchDetailLoading(true); setBatchCatFilter('all'); setBatchStatusFilter('all'); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${batchNo}/submissions`); if (res.data.success) { setBatchSubmissions(res.data.submissions || []); setBatchInfo({ batch_no: batchNo, batch_status: res.data.batch_status, batch_received_by: res.data.batch_received_by, batch_received_at: res.data.batch_received_at }); } } catch { message.error('Failed to load batch'); } finally { setBatchDetailLoading(false); } };
  /* User-initiated open: start the batch with a clean selection */
  const openBatchFresh = (batchNo) => { clearSelection(); openBatch(batchNo); };
  /* Silent reload — keeps the category / status filters (used after bulk actions) */
  const reloadBatchSubs = async (batchNo) => { try { const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${batchNo}/submissions`); if (res.data.success) setBatchSubmissions(res.data.submissions || []); } catch {} };
  const closeBatch = () => { setSelectedBatch(null); setBatchSubmissions([]); setBatchInfo(null); setBatchCatFilter('all'); setBatchStatusFilter('all'); clearSelection(); };
  const markBatchReceived = async (bn) => { try { const res = await axios.post(`${API_BASE_URL}/api/procurement/batches/${bn}/receive`, { received_by: username }); if (res.data.success) { message.success(`Batch ${bn} marked received`); fetchBatchSummaries(); if (selectedBatch === bn) openBatch(bn); } else message.error(res.data.message); } catch { message.error('Failed'); } };
  const reopenBatch = async (bn) => { try { const res = await axios.post(`${API_BASE_URL}/api/procurement/batches/${bn}/reopen`, { unmarked_by: username }); if (res.data.success) { message.success(`Batch ${bn} reopened`); fetchBatchSummaries(); if (selectedBatch === bn) openBatch(bn); } else message.error(res.data.message); } catch { message.error('Failed'); } };

  const openDetail = async (id) => { setDetailId(id); setDetailLoading(true); setReviewRemarks(''); setStatusRemarks(''); setHoldRemarks(''); setEditingField(null); setEditFieldValue(''); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions/${id}`); setDetailData(res.data.submission); } catch { message.error('Failed to load details'); } finally { setDetailLoading(false); } };
  const closeDetail = () => { setDetailId(null); setDetailData(null); setReviewRemarks(''); setStatusRemarks(''); setHoldRemarks(''); setEditingField(null); };

  /* Refresh everything after any status change */
  const refreshAfterChange = async () => {
    // A single review changes this invoice's status — drop it from any bulk selection
    if (detailId) removeFromSelection(detailId);
    await openDetail(detailId);
    fetchAllSubmissions(); fetchStats(); fetchBatchSummaries();
    if (selectedBatch) reloadBatchSubs(selectedBatch);
  };

  const startEditField = (f, v) => { setEditingField(f); setEditFieldValue(v || ''); };
  const cancelEditField = () => { setEditingField(null); setEditFieldValue(''); };
  const saveEditField = async (fieldName) => { if (!detailId) return; setEditFieldSaving(true); try { const res = await axios.patch(`${API_BASE_URL}/api/procurement/submissions/${detailId}`, { [fieldName]: editFieldValue.trim(), edited_by: username }); if (res.data.success) { message.success(`${fieldName.replace(/_/g,' ')} updated`); setEditingField(null); setEditFieldValue(''); await openDetail(detailId); fetchAllSubmissions(); fetchStats(); fetchBatchSummaries(); if (selectedBatch) reloadBatchSubs(selectedBatch); } else message.error(res.data.message || 'Update failed'); } catch { message.error('Failed to update field'); } finally { setEditFieldSaving(false); } };

  const renderEditableField = (fieldName, displayValue, options = {}) => {
    const { isBold, isAmount } = options;
    const canEdit = detailData && detailData.status !== 'deleted';
    if (editingField === fieldName) {
      return (<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Input size="small" value={editFieldValue} onChange={(e) => setEditFieldValue(e.target.value)} onPressEnter={() => saveEditField(fieldName)} style={{ borderRadius: 3, flex: 1, maxWidth: 260, fontSize: 12 }} autoFocus /><Button type="text" size="small" icon={<CheckOutlined style={{ color: '#52c41a' }} />} loading={editFieldSaving} onClick={() => saveEditField(fieldName)} style={{ padding: '0 4px' }} /><Button type="text" size="small" icon={<CloseOutlined style={{ color: '#ff4d4f' }} />} onClick={cancelEditField} style={{ padding: '0 4px' }} /></div>);
    }
    return (<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ color: displayValue ? (isBold ? '#1a1a1a' : '#333') : '#ccc', fontWeight: isBold ? 600 : 400, fontSize: isAmount ? 13 : 12 }}>{isAmount ? fmt(displayValue) : (displayValue || '-')}</span>{canEdit && <Tooltip title={`Edit ${fieldName.replace(/_/g,' ')}`}><Button type="text" size="small" icon={<EditOutlined style={{ fontSize: 10, color: '#bfbfbf' }} />} onClick={() => startEditField(fieldName, displayValue)} className="edit-field-btn" style={{ padding: '0 4px', opacity: 0 }} /></Tooltip>}</div>);
  };

  const handleReview = (decision) => { if (decision === 'rejected' && !reviewRemarks.trim()) { message.warning('Remarks required for rejection'); return; } Modal.confirm({ title: `${decision === 'approved' ? 'Approve' : 'Reject'} this submission?`, content: <div style={{ fontSize: 12, fontFamily: FONT_FAMILY }}><div>Ref: <strong>{detailData?.submission_ref}</strong></div><div>Vendor: <strong>{detailData?.vendor_name || '-'}</strong></div><div>Amount: <strong>{fmt(detailData?.total_amount)}</strong></div>{detailData?.status === 'on_hold' && <div style={{ marginTop: 6, color: HOLD_COLOR }}>This releases the current hold.</div>}{reviewRemarks && <div style={{ marginTop: 6, color: '#888' }}>Remarks: {reviewRemarks}</div>}</div>, okText: decision === 'approved' ? 'Approve' : 'Reject', okButtonProps: { danger: decision === 'rejected' }, onOk: async () => { setReviewLoading(true); try { await axios.post(`${API_BASE_URL}/api/procurement/submissions/${detailId}/review`, { decision, remarks: reviewRemarks.trim(), reviewed_by: username }); message.success(`Submission ${decision}`); setReviewRemarks(''); await refreshAfterChange(); } catch (e) { message.error(e.response?.data?.message || 'Failed'); } finally { setReviewLoading(false); } } }); };

  const handleChangeStatus = (newStatus) => {
    if (newStatus === 'rejected' && !statusRemarks.trim()) { message.warning('Remarks required to reject'); return; }
    Modal.confirm({
      title: `Change status to ${STATUS_CFG[newStatus]?.label || newStatus}?`,
      content: (
        <div style={{ fontSize: 12, fontFamily: FONT_FAMILY }}>
          <div>Ref: <strong>{detailData?.submission_ref}</strong></div>
          <div>Current: <strong>{STATUS_CFG[detailData?.status]?.label || detailData?.status}</strong></div>
          <div>New: <strong>{STATUS_CFG[newStatus]?.label || newStatus}</strong></div>
          {statusRemarks && <div style={{ marginTop: 6, color: '#888' }}>Remarks: {statusRemarks}</div>}
        </div>
      ),
      okText: 'Confirm',
      okButtonProps: { danger: newStatus === 'rejected' },
      onOk: async () => {
        setStatusChangeLoading(true);
        try {
          const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${detailId}/change-status`, {
            status: newStatus, remarks: statusRemarks.trim(), changed_by: username,
          });
          if (res.data.success) {
            message.success(res.data.message || 'Status updated');
            setStatusRemarks('');
            await refreshAfterChange();
          } else message.error(res.data.message || 'Failed');
        } catch (e) { message.error(e.response?.data?.message || 'Failed'); }
        finally { setStatusChangeLoading(false); }
      },
    });
  };

  /* ═══════════════════════════════════════════
     v15 — HOLD / RELEASE
     ═══════════════════════════════════════════ */
  const holdPreviewText = (note) => buildHoldMessage(
    detailData?.vendor_name, detailData?.invoice_number, detailData?.submission_ref, note,
  );

  const handleHold = () => {
    const note = holdRemarks.trim();
    Modal.confirm({
      title: 'Put this invoice on hold?',
      icon: <PauseCircleOutlined style={{ color: HOLD_COLOR }} />,
      width: 520,
      content: (
        <div style={{ fontSize: 12, fontFamily: FONT_FAMILY }}>
          <div>Ref: <strong>{detailData?.submission_ref}</strong></div>
          <div>Amount: <strong>{fmt(detailData?.total_amount)}</strong></div>
          <div style={{ marginTop: 8, ...S.subLabel }}>The uploader will see</div>
          <div style={{ marginTop: 4, padding: '7px 10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 3, lineHeight: 1.5 }}>
            {holdPreviewText(note)}
          </div>
          {(detailData?.status === 'approved' || detailData?.status === 'rejected') && (
            <div style={{ marginTop: 8, color: HOLD_COLOR }}>
              This overrides the existing {STATUS_CFG[detailData?.status]?.label} decision.
            </div>
          )}
        </div>
      ),
      okText: 'Put On Hold',
      okButtonProps: { style: { background: HOLD_COLOR, borderColor: HOLD_COLOR, color: '#fff' } },
      onOk: async () => {
        setHoldLoading(true);
        try {
          const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${detailId}/hold`, {
            remarks: note, held_by: username,
          });
          if (res.data.success) {
            message.success('Invoice put on hold');
            setHoldRemarks('');
            await refreshAfterChange();
          } else message.error(res.data.message || 'Failed to put on hold');
        } catch (e) { message.error(e.response?.data?.message || 'Failed to put on hold'); }
        finally { setHoldLoading(false); }
      },
    });
  };

  const handleReleaseHold = () => {
    Modal.confirm({
      title: 'Release this hold?',
      icon: <PlayCircleOutlined style={{ color: '#237804' }} />,
      content: (
        <div style={{ fontSize: 12, fontFamily: FONT_FAMILY }}>
          <div>Ref: <strong>{detailData?.submission_ref}</strong></div>
          <div style={{ marginTop: 6 }}>It goes back to <strong>Pending Review</strong> and the hold note is cleared for the uploader.</div>
          {holdRemarks.trim() && <div style={{ marginTop: 6, color: '#888' }}>Remarks: {holdRemarks.trim()}</div>}
        </div>
      ),
      okText: 'Release Hold',
      onOk: async () => {
        setHoldLoading(true);
        try {
          const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${detailId}/release-hold`, {
            remarks: holdRemarks.trim(), released_by: username,
          });
          if (res.data.success) {
            message.success('Hold released — back in the review queue');
            setHoldRemarks('');
            await refreshAfterChange();
          } else message.error(res.data.message || 'Failed to release hold');
        } catch (e) { message.error(e.response?.data?.message || 'Failed to release hold'); }
        finally { setHoldLoading(false); }
      },
    });
  };

  /* ═══════════════════════════════════════════
     v16 / v16.1 — BULK APPROVE / BULK REJECT / BULK FLIP
     ═══════════════════════════════════════════ */
  const openBulk = (mode) => {
    if (!selectedRows.length) { message.info('Select at least one invoice'); return; }
    if (!selectedRows.some(r => r.status !== mode)) {
      message.info(`All selected invoices are already ${mode === 'approved' ? 'Approved' : 'Rejected'}`);
      return;
    }
    setBulkMode(mode);
    setBulkCommon('');
    setBulkResults({});
  };

  const closeBulk = () => {
    if (bulkRunning) return;
    setBulkMode(null);
    setBulkResults({});
    setBulkCommon('');
  };

  const setRowRemark = (id, text) => setBulkRemarks(prev => ({ ...prev, [id]: text }));

  const applyCommonRemark = (onlyEmpty) => {
    const text = bulkCommon.trim();
    if (!text) { message.warning('Type a common remark first'); return; }
    setBulkRemarks(prev => {
      const next = { ...prev };
      bulkActionRows.forEach(r => {
        if (bulkResults[r.id]?.ok) return;
        if (!onlyEmpty || !(next[r.id] || '').trim()) next[r.id] = text;
      });
      return next;
    });
  };

  const bulkMissingCount = bulkMode === 'rejected'
    ? bulkActionRows.filter(r => !(bulkRemarks[r.id] || '').trim()).length
    : 0;

  const executeBulk = async () => {
    const mode = bulkMode;
    const queue = [...bulkActionRows];
    const skipped = [...bulkSkipRows];
    setBulkRunning(true);
    setBulkResults({});
    const results = {};
    let cursor = 0;

    const worker = async () => {
      while (cursor < queue.length) {
        const r = queue[cursor++];
        const own = (bulkRemarks[r.id] || '').trim();
        const remark = own || (mode === 'approved' ? bulkCommon.trim() : '');
        try {
          const res = isDecided(r)
            // Approved ⇄ Rejected → override endpoint (same one the detail drawer uses)
            ? await axios.post(`${API_BASE_URL}/api/procurement/submissions/${r.id}/change-status`, {
                status: mode, remarks: remark, changed_by: username,
              })
            // Pending / Under Review / On Hold → normal review
            : await axios.post(`${API_BASE_URL}/api/procurement/submissions/${r.id}/review`, {
                decision: mode, remarks: remark, reviewed_by: username,
              });
          if (res.data && res.data.success === false) {
            results[r.id] = { ok: false, error: res.data.message || 'Rejected by server' };
          } else {
            results[r.id] = { ok: true };
          }
        } catch (e) {
          results[r.id] = { ok: false, error: e.response?.data?.message || e.response?.data?.detail || e.message || 'Failed' };
        }
        setBulkResults(prev => ({ ...prev, [r.id]: results[r.id] }));
      }
    };

    await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, queue.length) }, worker));

    const okSet = new Set(Object.keys(results).filter(id => results[id].ok).map(String));
    const okCount = okSet.size;
    const failCount = queue.length - okCount;
    const verb = mode === 'approved' ? 'approved' : 'rejected';

    // Update global search rows in place so their status reflects immediately
    setGlobalResults(prev => prev.map(s => okSet.has(String(s.id)) ? { ...s, status: mode } : s));

    // Drop successful rows + skipped (already-in-target) rows from the selection
    removeFromSelection([
      ...queue.filter(r => okSet.has(String(r.id))).map(r => r.id),
      ...skipped.map(r => r.id),
    ]);
    setBulkRunning(false);

    fetchAllSubmissions(); fetchStats(); fetchBatchSummaries();
    if (selectedBatch) reloadBatchSubs(selectedBatch);

    if (failCount === 0) {
      message.success(`${okCount} invoice${okCount !== 1 ? 's' : ''} ${verb}`);
      setBulkMode(null);
      setBulkResults({});
      setBulkCommon('');
    } else {
      message.warning(`${okCount} ${verb}, ${failCount} failed — failed ones are still listed, fix and retry`, 6);
    }
  };

  const runBulk = () => {
    if (!bulkActionRows.length) return;
    if (bulkMode === 'rejected' && bulkMissingCount > 0) {
      message.warning(`${bulkMissingCount} invoice${bulkMissingCount > 1 ? 's need' : ' needs'} a rejection remark`);
      return;
    }
    const isReject = bulkMode === 'rejected';
    const n = bulkActionRows.length;
    const heldCount = bulkActionRows.filter(r => r.status === 'on_hold').length;
    const flipCount = bulkActionRows.filter(isDecided).length;
    Modal.confirm({
      title: `${isReject ? 'Reject' : 'Approve'} ${n} invoice${n > 1 ? 's' : ''}?`,
      content: (
        <div style={{ fontSize: 12, fontFamily: FONT_FAMILY }}>
          <div>Total value: <strong>{fmt(bulkActionTotal)}</strong></div>
          {isReject && <div style={{ marginTop: 4 }}>Each uploader will see the remark you wrote for their invoice.</div>}
          {flipCount > 0 && (
            <div style={{ marginTop: 6, color: '#cf1322' }}>
              {flipCount} {flipCount > 1 ? 'are' : 'is'} currently {isReject ? 'Approved' : 'Rejected'} — this overrides that decision ({isReject ? 'Approved → Rejected' : 'Rejected → Approved'}).
            </div>
          )}
          {heldCount > 0 && <div style={{ marginTop: 6, color: HOLD_COLOR }}>{heldCount} of these {heldCount > 1 ? 'are' : 'is'} on hold — this releases the hold.</div>}
          {bulkSkipRows.length > 0 && <div style={{ marginTop: 6, color: '#888' }}>{bulkSkipRows.length} already {isReject ? 'Rejected' : 'Approved'} — skipped.</div>}
        </div>
      ),
      okText: isReject ? 'Reject All' : 'Approve All',
      okButtonProps: isReject ? { danger: true } : { style: { background: '#237804', borderColor: '#237804' } },
      onOk: executeBulk,
    });
  };

  const openPreview = async (subId, startIdx = 0) => { setPreviewLoading(true); setPreviewVisible(true); setActiveDocIdx(startIdx); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions/${subId}/preview-all`); if (res.data.success) setPreviewDocs(res.data.documents || []); else message.error('Failed to load previews'); } catch { message.error('Preview failed'); } finally { setPreviewLoading(false); } };
  const downloadDoc = (doc) => { const url = doc?.download_url || doc?.url; if (url) { const a = document.createElement('a'); a.href = url; a.download = doc.filename || 'document'; a.target = '_blank'; document.body.appendChild(a); a.click(); document.body.removeChild(a); } };

  /* ═══════════════════════════════════════════
     v15.1 — PRINT INVOICE DETAIL SHEET (A4)
     Opens a clean window built only from detailData and triggers the print dialog.
     ═══════════════════════════════════════════ */
  const handlePrintInvoice = () => {
    if (!detailData) { message.warning('Open an invoice first'); return; }
    const html = buildPrintInvoiceHtml(detailData);
    // No `noopener` here — we need the window handle to write the document into it.
    const win = window.open('', '_blank');
    if (!win) { message.warning('Please allow pop-ups to print this invoice.'); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  const handleSaveSubmissionPdf = async (submissionId, batchNo) => {
    setSavingDocId(submissionId);
    try {
      const streamUrl = `${API_BASE_URL}/api/procurement/submissions/${submissionId}/stream`;
      const result = await saveToLinkedFolder(streamUrl, `submission-${submissionId}.pdf`, batchNo);
      try {
        const headRes = await fetch(streamUrl, { method: 'HEAD' });
        const actualFilename = headRes.headers.get('x-filename') || `submission-${submissionId}.pdf`;
        if (result.method === 'folder') {
          const folderName = batchNo ? `Batch-${batchNo}` : 'No-Batch';
          message.success(`Saved to ${rootDirName}/${folderName}/${actualFilename}`, 4);
        } else message.success(`Downloading ${actualFilename}`);
      } catch {
        if (result.method === 'folder') message.success(`Saved to ${rootDirName}/${result.path}`, 4);
        else message.success('Download started');
      }
    } catch { message.error('Failed to save'); }
    finally { setSavingDocId(null); }
  };

  const handleSaveDocFile = async (docId, batchNo) => {
    setSavingDocId(docId);
    try {
      const streamUrl = `${API_BASE_URL}/api/procurement/documents/${docId}/stream`;
      let filename = `document-${docId}.pdf`;
      try { const headRes = await fetch(streamUrl, { method: 'HEAD' }); filename = headRes.headers.get('x-filename') || filename; } catch {}
      const result = await saveToLinkedFolder(streamUrl, filename, batchNo);
      if (result.method === 'folder') message.success(`Saved to ${rootDirName}/${result.path}`, 4);
      else message.success(`Downloading ${filename}`);
    } catch { message.error('Failed to save'); }
    finally { setSavingDocId(null); }
  };

  const handleDownloadExcel = async (batchNo) => { setDownloadingExcel(batchNo); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${batchNo}/report/excel`, { responseType: 'blob', timeout: 60000 }); let fn = res.headers['x-excel-filename'] || `Batch-${batchNo}-Report.xlsx`; const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = fn; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url); message.success(`Downloaded: ${fn}`); } catch (err) { if (err.response?.status === 404) message.warning(`No invoices in Batch ${batchNo}`); else message.error('Excel download failed'); } finally { setDownloadingExcel(null); } };
  const handleDownloadPdf = async (batchNo) => { setDownloadingPdf(batchNo); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/batches/${batchNo}/report/pdf`, { responseType: 'blob', timeout: 60000 }); let fn = res.headers['x-pdf-filename'] || `Batch-${batchNo}-report.pdf`; const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = fn; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url); message.success(`Downloaded: ${fn}`); } catch (err) { if (err.response?.status === 404) message.warning(`No invoices in Batch ${batchNo}`); else message.error('PDF download failed'); } finally { setDownloadingPdf(null); } };
  const handleDownloadAllExcel = async () => { setDownloadingAllExcel(true); try { const res = await axios.get(`${API_BASE_URL}/api/procurement/report/excel/all`, { responseType: 'blob', timeout: 120000 }); let fn = res.headers['x-excel-filename'] || 'All-Batches-Report.xlsx'; const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = fn; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url); message.success(`Downloaded: ${fn}`); } catch (err) { if (err.response?.status === 404) message.warning('No submissions'); else message.error('Download failed'); } finally { setDownloadingAllExcel(false); } };

  /* ═══════════════════════════════════════════
     COMPUTED — RECEIVED-BATCH-ONLY STATS
     The top card row shows counts for RECEIVED batches only. Pending / Approved /
     Rejected / On Hold / Value all exclude OPEN batches (e.g. Batch 128's pending).
     ═══════════════════════════════════════════ */
  const filteredBatches = useMemo(() => {
  const base = batchSearch
    ? batchSummaries.filter(b => String(b.batch_no).includes(batchSearch))
    : batchSummaries;
  return [...base].sort((a, b) => (b.batch_no || 0) - (a.batch_no || 0));
}, [batchSummaries, batchSearch]);

  /* v16.2 — batch_no → 'open' | 'received' (for export scope + Batch Status column) */
  const batchStatusMap = useMemo(() => {
    const m = {};
    batchSummaries.forEach(b => { m[b.batch_no] = b.batch_status || 'open'; });
    return m;
  }, [batchSummaries]);

  /* v16.2 — live count for the status-export bar (from batch summaries, no extra API call).
     Category filter can't be counted from summaries, so the count shows only when category = all. */
  const exportPreviewCount = useMemo(() => {
    if (exportCategory !== 'all') return null;
    const key = { all: 'total_submissions', pending: 'pending_count', on_hold: 'on_hold_count', approved: 'approved_count', rejected: 'rejected_count' }[exportStatus];
    return batchSummaries
      .filter(b => exportScope === 'all' || (b.batch_status || 'open') === exportScope)
      .reduce((s, b) => s + (Number(b[key]) || 0), 0);
  }, [batchSummaries, exportStatus, exportCategory, exportScope]);

  const receivedStats = useMemo(() => {
    const received = batchSummaries.filter(b => (b.batch_status || 'open') === 'received');
    return {
      total:     received.reduce((s, b) => s + (b.total_submissions || 0), 0),
      pending:   received.reduce((s, b) => s + (b.pending_count      || 0), 0),
      onHold:    received.reduce((s, b) => s + (b.on_hold_count      || 0), 0),
      approved:  received.reduce((s, b) => s + (b.approved_count     || 0), 0),
      rejected:  received.reduce((s, b) => s + (b.rejected_count     || 0), 0),
      totalValue: received.reduce((s, b) => s + (Number(b.total_value) || 0), 0),
      batchCount: received.length,
    };
  }, [batchSummaries]);

  const batchCategoryBreakdown = useMemo(() => { if (!batchSubmissions.length) return []; const map = {}; batchSubmissions.forEach(s => { const c = s.category || 'other'; if (!map[c]) map[c] = { category: c, items: [], pending: 0, onHold: 0, approved: 0, rejected: 0, value: 0 }; map[c].items.push(s); if (s.status === 'pending_review' || s.status === 'under_review') map[c].pending++; else if (s.status === 'on_hold') map[c].onHold++; else if (s.status === 'approved') map[c].approved++; else if (s.status === 'rejected') map[c].rejected++; map[c].value += Number(s.total_amount) || 0; }); return Object.values(map).sort((a, b) => b.items.length - a.items.length); }, [batchSubmissions]);
  /* v16.2 — category AND status filter */
  const filteredBatchSubs = useMemo(
    () => batchSubmissions.filter(s =>
      (batchCatFilter === 'all' || s.category === batchCatFilter) &&
      matchesStatusFilter(s.status, batchStatusFilter)),
    [batchSubmissions, batchCatFilter, batchStatusFilter],
  );
  /* status counts within the current category (for the status filter buttons) */
  const batchStatusCounts = useMemo(() => {
    const inCat = batchSubmissions.filter(s => batchCatFilter === 'all' || s.category === batchCatFilter);
    const c = {};
    STATUS_FILTER_OPTIONS.forEach(o => { c[o.value] = inCat.filter(s => matchesStatusFilter(s.status, o.value)).length; });
    return c;
  }, [batchSubmissions, batchCatFilter]);
  const filteredAllSubs = useMemo(() => { if (!allSearchText.trim()) return allSubmissions; const q = allSearchText.toLowerCase(); return allSubmissions.filter(s => (s.submission_ref||'').toLowerCase().includes(q)||(s.vendor_name||'').toLowerCase().includes(q)||(s.lpo_number||'').toLowerCase().includes(q)||(s.invoice_number||'').toLowerCase().includes(q)||String(s.batch_no||'').includes(q)); }, [allSubmissions, allSearchText]);

  const totalBatches = batchSummaries.length;
  const openBatches = batchSummaries.filter(b => (b.batch_status || 'open') !== 'received').length;
  const receivedBatches = totalBatches - openBatches;
  const totalInvoices = batchSummaries.reduce((s, b) => s + b.total_submissions, 0);
  const totalValueAll = batchSummaries.reduce((s, b) => s + b.total_value, 0);

  /* ═══════════════════════════════════════════
     v16.2 — EXPORT BY STATUS
     ═══════════════════════════════════════════ */
  /* Pull every submission for a status/category across all batches (paged) */
  const fetchAllForExport = async (statusFilter, categoryFilter) => {
    const out = [];
    const seen = new Set();
    for (const st of apiStatusesFor(statusFilter)) {
      let offset = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const params = { limit: EXPORT_PAGE_SIZE, offset };
        if (st) params.status = st;
        if (categoryFilter !== 'all') params.category = categoryFilter;
        const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, { params, timeout: 120000 });
        const page = res.data.submissions || [];
        page.forEach(r => { if (!seen.has(r.id)) { seen.add(r.id); out.push(r); } });
        const total = Number(res.data.total);
        offset += page.length;
        if (page.length < EXPORT_PAGE_SIZE || (!isNaN(total) && offset >= total) || page.length === 0) break;
      }
    }
    return out;
  };

  const sortForExport = (rows) => [...rows].sort((a, b) =>
    (Number(b.batch_no) || 0) - (Number(a.batch_no) || 0) ||
    (a.vendor_name || '').localeCompare(b.vendor_name || '') ||
    (a.invoice_number || '').localeCompare(b.invoice_number || ''));

  const handleExportByStatus = async () => {
    setExportingStatus(true);
    const hide = message.loading('Collecting invoices…', 0);
    try {
      let rows = await fetchAllForExport(exportStatus, exportCategory);
      // client-side safety net on status (API may ignore unknown filters)
      rows = rows.filter(r => matchesStatusFilter(r.status, exportStatus));
      if (exportScope !== 'all') rows = rows.filter(r => r.batch_no && (batchStatusMap[r.batch_no] || 'open') === exportScope);
      if (!rows.length) { message.warning('No invoices match this filter'); return; }
      rows = sortForExport(rows);

      const statusLbl = STATUS_FILTER_LABEL[exportStatus];
      const catLbl = exportCategory === 'all' ? 'All Categories' : (CATEGORY_LABELS[exportCategory] || exportCategory);
      const scopeLbl = exportScope === 'all' ? 'All Batches' : exportScope === 'received' ? 'Received Batches' : 'Open Batches';
      const filename = [
        statusLbl === 'All Statuses' ? 'All' : statusLbl, 'Invoices',
        exportCategory !== 'all' ? safeFilePart(catLbl) : null,
        safeFilePart(scopeLbl), todayDubai(),
      ].filter(Boolean).join('-') + '.xlsx';

      exportInvoicesXlsx(rows, {
        title: `${statusLbl === 'All Statuses' ? 'All' : statusLbl} Invoices — ${scopeLbl}`,
        filterLine: `Status: ${statusLbl} · Category: ${catLbl} · Scope: ${scopeLbl} · ${rows.length} invoices · ${fmt(rows.reduce((s, r) => s + (Number(r.total_amount) || 0), 0))}`,
        filename,
        batchStatusMap,
      });
      message.success(`Downloaded ${filename} (${rows.length} invoices)`);
    } catch (e) {
      console.error(e);
      message.error('Export failed');
    } finally {
      hide();
      setExportingStatus(false);
    }
  };

  /* Batch drawer — export exactly the rows on screen */
  const handleExportBatchFiltered = () => {
    if (!filteredBatchSubs.length) { message.warning('Nothing to export for this filter'); return; }
    const statusLbl = STATUS_FILTER_LABEL[batchStatusFilter];
    const catLbl = batchCatFilter === 'all' ? 'All Categories' : (CATEGORY_LABELS[batchCatFilter] || batchCatFilter);
    const rows = sortForExport(filteredBatchSubs);
    const filename = [
      `Batch-${selectedBatch}`,
      batchStatusFilter !== 'all' ? statusLbl : 'All',
      batchCatFilter !== 'all' ? safeFilePart(catLbl) : null,
    ].filter(Boolean).join('-') + '.xlsx';
    exportInvoicesXlsx(rows, {
      title: `Batch ${selectedBatch} — ${batchStatusFilter !== 'all' ? statusLbl : 'All'} Invoices`,
      filterLine: `Status: ${statusLbl} · Category: ${catLbl} · ${rows.length} invoices · ${fmt(rows.reduce((s, r) => s + (Number(r.total_amount) || 0), 0))}`,
      filename,
      batchStatusMap,
    });
    message.success(`Downloaded ${filename}`);
  };

  /* ═══════════════════════════════════════════
     COLUMNS — plain (monochrome) mode support
     ═══════════════════════════════════════════ */
  const makeSubColumns = (opts = {}) => {
    const { showBatch = true, showCategory = true, plain = false } = opts;

    const boldBlack = { color: '#000', fontWeight: 700 };
    const black = { color: '#000' };
    const YELLOW = 'pts-col-yellow'; // faint-yellow core columns (CSS below)

    const cols = [];

    if (showBatch) cols.push({ title: 'Batch', dataIndex: 'batch_no', key: 'bn', width: 70,
      render: v => v ? (plain ? <span style={{ ...boldBlack, fontSize: 12 }}>{v}</span>
        : <Tag style={{ borderRadius: 3, fontWeight: 700, fontSize: 11, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>{v}</Tag>) : '-' });

    // Ref column dropped entirely in plain mode
    if (!plain) cols.push({ title: 'Ref', dataIndex: 'submission_ref', key: 'ref', width: 130, fixed: 'left',
      render: (t, r) => <a onClick={() => openDetail(r.id)} style={{ fontWeight: 600, fontSize: 12, color: '#1a5276', cursor: 'pointer' }}>{t}</a> });

    // ── CORE (always-visible) columns — faint yellow in plain mode ──
    if (showCategory) cols.push({ title: 'Category', dataIndex: 'category', key: 'cat', width: 120,
      className: plain ? YELLOW : undefined,
      render: c => plain ? <span style={{ ...boldBlack, fontSize: 12 }}>{CATEGORY_LABELS[c] || c}</span>
        : <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: CATEGORY_COLORS[c] || '#666', background: '#fff', border: `1px solid ${CATEGORY_COLORS[c] || '#d9d9d9'}` }}>{CATEGORY_LABELS[c] || c}</Tag> });

    cols.push(
      { title: 'LPO', dataIndex: 'lpo_number', key: 'lpo', width: 75, className: plain ? YELLOW : undefined,
        render: t => <span style={{ ...boldBlack, fontSize: 12 }}>{t || '—'}</span> },

      { title: 'Vendor', dataIndex: 'vendor_name', key: 'v', ellipsis: true, width: 185, className: plain ? YELLOW : undefined,
        sorter: (a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''),
        defaultSortOrder: plain ? 'ascend' : undefined,
        render: t => plain ? <span style={{ ...boldBlack, fontSize: 12, textTransform: 'uppercase' }}>{t || '—'}</span>
          : <span style={{ fontSize: 12, color: t ? '#1a1a1a' : '#ccc' }}>{t || '-'}</span> },

      { title: 'Invoice No.', dataIndex: 'invoice_number', key: 'inv', width: 115, className: plain ? YELLOW : undefined,
        render: t => plain ? <span style={{ ...boldBlack, fontSize: 12 }}>{t || '—'}</span>
          : (t ? <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #91caff' }}>{t}</Tag> : <span style={{ color: '#ccc', fontSize: 11 }}>—</span>) },

      { title: 'Inv. Date', dataIndex: 'invoice_date', key: 'id', width: 88, className: plain ? YELLOW : undefined,
        render: t => <span style={{ ...(plain ? boldBlack : { color: '#555' }), fontSize: plain ? 12 : 11 }}>{t || '—'}</span> },

      // Currency column — single AED label for the amount group
      { title: plain ? 'Cur' : 'AED', key: 'cur', width: 48, align: 'center', className: plain ? YELLOW : undefined,
        render: () => <span style={{ ...boldBlack, fontSize: 11 }}>AED</span> },

      { title: 'Total', dataIndex: 'total_amount', key: 'amt', width: 105, align: 'right', className: plain ? YELLOW : undefined,
        sorter: (a, b) => (Number(a.total_amount)||0) - (Number(b.total_amount)||0),
        render: v => plain ? <span style={{ ...boldBlack, fontSize: 12 }}>{fmtNum(v)}</span>
          : <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{fmt(v)}</span> },

      // VAT header carries the +/- toggle (last core column before the extras)
      { title: plain
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
              VAT
              <span onClick={(e) => { e.stopPropagation(); setShowExtraCols(v => !v); }}
                title={showExtraCols ? 'Hide extra columns' : 'Show more columns'}
                style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13, lineHeight: 1,
                  border: '1px solid #000', borderRadius: 3, padding: '0 4px', userSelect: 'none' }}>
                {showExtraCols ? '−' : '+'}
              </span>
            </span>
          : 'VAT',
        dataIndex: 'vat_amount', key: 'vat', width: 90, align: 'right', className: plain ? YELLOW : undefined,
        render: v => plain ? <span style={{ ...boldBlack, fontSize: 12 }}>{fmtNum(v)}</span>
          : <span style={{ fontSize: 11, color: '#555' }}>{fmt(v)}</span> },
    );

    // ── EXTRA (toggle) columns — only when expanded in plain mode; always in non-plain ──
    if (!plain || showExtraCols) {
      cols.push(
        { title: 'TRN', dataIndex: 'vendor_trn', key: 'trn', width: 140,
          render: t => t ? <span style={{ ...(plain ? black : { color: '#1a5276' }), fontSize: plain ? 11 : 10, fontFamily: 'monospace' }}>{t}</span>
            : <span style={{ color: plain ? '#000' : '#d9d9d9' }}>—</span> },
        { title: 'DN No.', dataIndex: 'dn_number', key: 'dn', width: 90,
          render: t => <span style={{ ...(plain ? black : { color: '#555' }), fontSize: plain ? 12 : 11 }}>{t || '—'}</span> },
        { title: 'DN Date', dataIndex: 'dn_date', key: 'dnd', width: 85,
          render: t => <span style={{ ...(plain ? black : { color: '#555' }), fontSize: plain ? 12 : 11 }}>{t || '—'}</span> },
        { title: 'Status', dataIndex: 'status', key: 'st', width: 110,
          render: s => plain
            ? <span style={{ ...boldBlack, fontSize: 12, ...(s === 'on_hold' ? { color: HOLD_COLOR } : {}) }}>{STATUS_CFG[s]?.label || s || '—'}</span>
            : statusTag(s) },
        { title: 'Rev', dataIndex: 'revision_count', key: 'rv', width: 45, align: 'center',
          render: v => v > 0 ? (plain ? <span style={{ ...black, fontSize: 12 }}>#{v}</span>
            : <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#531dab', background: '#f9f0ff', border: '1px solid #d3adf7' }}>#{v}</Tag>) : '-' },
        { title: 'By', dataIndex: 'submitted_by', key: 'sb', width: 100, ellipsis: true,
          render: t => <span style={{ ...(plain ? black : { color: '#666' }), fontSize: plain ? 12 : 11 }}>{t ? t.split('@')[0] : '-'}</span> },
        { title: 'Date', dataIndex: 'submitted_at', key: 'dt', width: 90,
          render: v => <span style={{ ...(plain ? black : { color: '#888' }), fontSize: plain ? 12 : 11 }}>{fmtDate(v)}</span> },
        { title: 'Remarks', dataIndex: 'accounts_remarks', key: 'rem', width: 130, ellipsis: true,
          render: t => t ? <Tooltip title={t}><span style={{ ...(plain ? black : { color: '#cf1322' }), fontSize: plain ? 12 : 11 }}>{t}</span></Tooltip> : '-' },
      );
    }

    // ── Actions (always) ──
    cols.push({ title: '', key: 'act', width: 100, fixed: 'right', render: (_, r) => (
      <Space size={2}>
        <Tooltip title="View Details"><Button type="text" size="small" icon={<EyeOutlined style={{ color: plain ? '#000' : '#1a5276' }} />} onClick={() => openDetail(r.id)} /></Tooltip>
        <Tooltip title="Preview"><Button type="text" size="small" icon={<FileTextOutlined style={{ color: plain ? '#000' : '#888' }} />} onClick={() => openPreview(r.id)} /></Tooltip>
        <Tooltip title={rootDirHandle ? `Save to ${rootDirName}/Batch-${r.batch_no}/` : 'Download PDF'}>
          <Button type="text" size="small"
            icon={savingDocId === r.id ? <LoadingOutlined spin style={{ color: '#000' }} /> : <SaveOutlined style={{ color: plain ? '#000' : (rootDirHandle ? '#1a5276' : '#237804') }} />}
            onClick={() => handleSaveSubmissionPdf(r.id, r.batch_no)} />
        </Tooltip>
      </Space>
    )});

    return cols;
  };

  /* Held rows get a faint amber tint; selected rows a faint blue */
  const rowClass = (record) => {
    const cls = [];
    if (record.status === 'on_hold') cls.push('pts-row-hold');
    if (selectedIds.includes(record.id)) cls.push('pts-row-selected');
    return cls.join(' ');
  };

  /* ═══════════════════════════════════════════
     FOLDER SETTINGS BAR
     ═══════════════════════════════════════════ */
  const renderFolderBar = () => (
    <div style={{
      background: rootDirHandle ? '#f6ffed' : '#fffbe6',
      border: `1px solid ${rootDirHandle ? '#b7eb8f' : '#ffe58f'}`,
      borderRadius: 3, padding: '6px 14px', marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      fontFamily: FONT_FAMILY,
    }}>
      {rootDirHandle ? (<>
        <LinkOutlined style={{ color: '#237804', fontSize: 12 }} />
        <span style={{ fontSize: 11, color: '#237804', fontWeight: 600 }}>Download folder linked:</span>
        <Tag style={{ borderRadius: 3, fontSize: 11, fontWeight: 600, margin: 0, padding: '0 6px', lineHeight: '18px', color: '#237804', background: '#fff', border: '1px solid #b7eb8f' }}>
          <FolderOpenOutlined style={{ marginRight: 4 }} />{rootDirName}
        </Tag>
        <span style={{ fontSize: 10, color: '#888' }}>PDFs auto-save to Batch-N subfolders. Existing files overwritten.</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Button size="small" icon={<FolderAddOutlined />} onClick={pickDownloadFolder} style={{ ...S.plainBtn }}>Change</Button>
          <Button size="small" icon={<DisconnectOutlined />} onClick={disconnectFolder} style={{ ...S.plainBtn, color: '#cf1322', borderColor: '#ffa39e' }}>Disconnect</Button>
        </div>
      </>) : (<>
        <SettingOutlined style={{ color: '#d46b08', fontSize: 12 }} />
        <span style={{ fontSize: 11, color: '#d46b08', fontWeight: 500 }}>No download folder linked</span>
        <span style={{ fontSize: 10, color: '#888' }}>Link a folder to auto-save PDFs into Batch-N subfolders.</span>
        <Button size="small" type="primary" icon={<FolderAddOutlined />} onClick={pickDownloadFolder}
          style={{ marginLeft: 'auto', ...S.plainBtn, background: '#1a5276', borderColor: '#1a5276' }}>
          {isFileSystemAccessSupported() ? 'Link Folder' : 'Use Chrome'}
        </Button>
      </>)}
    </div>
  );

  /* ═══════════════════════════════════════════
     STATS ROW — RECEIVED BATCHES ONLY
     ═══════════════════════════════════════════ */
  const renderStats = () => (
    <div>
      <div style={{
        padding: '6px 14px', marginBottom: 0,
        background: '#e8f4fd', border: '1px solid #7fb3d3',
        borderTopLeftRadius: 3, borderTopRightRadius: 3,
        borderBottom: 'none',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <InboxOutlined style={{ color: '#1a5276', fontSize: 12 }} />
        <span style={{ fontSize: 11, color: '#1a5276', fontWeight: 600 }}>
          Showing counts for RECEIVED batches only ({receivedStats.batchCount} batches received)
        </span>
        <span style={{ fontSize: 10, color: '#888', marginLeft: 'auto' }}>
          Open batches are excluded from these totals
        </span>
      </div>

      <div style={{ ...S.statRow, borderTopLeftRadius: 0, borderTopRightRadius: 0, marginBottom: 12 }}>
        <div style={S.statCell(false)}>
          <div style={S.statLabel}>Submissions (Received)</div>
          <div style={S.statValue()}>{receivedStats.total}</div>
          <div style={S.statSub}>of {stats?.total || 0} overall</div>
        </div>
        <div style={S.statCell(false)}>
          <div style={S.statLabel}>Pending Review</div>
          <div style={S.statValue(receivedStats.pending > 0 ? '#d46b08' : '#bfbfbf')}>{receivedStats.pending}</div>
          <div style={S.statSub}>in received batches</div>
        </div>
        <div style={S.statCell(false)}>
          <div style={S.statLabel}>On Hold</div>
          <div style={S.statValue(receivedStats.onHold > 0 ? HOLD_COLOR : '#bfbfbf')}>{receivedStats.onHold}</div>
          <div style={S.statSub}>{stats?.on_hold ? `${stats.on_hold} overall` : 'parked by accounts'}</div>
        </div>
        <div style={S.statCell(false)}>
          <div style={S.statLabel}>Approved</div>
          <div style={S.statValue(receivedStats.approved > 0 ? '#237804' : '#bfbfbf')}>{receivedStats.approved}</div>
          <div style={S.statSub}>in received batches</div>
        </div>
        <div style={S.statCell(false)}>
          <div style={S.statLabel}>Rejected</div>
          <div style={S.statValue(receivedStats.rejected > 0 ? '#cf1322' : '#bfbfbf')}>{receivedStats.rejected}</div>
          <div style={S.statSub}>in received batches</div>
        </div>
        <div style={S.statCell(true)}>
          <div style={S.statLabel}>Total Value (Received)</div>
          <div style={{ ...S.statValue('#1a5276'), fontSize: 13 }}>{fmt(receivedStats.totalValue)}</div>
          <div style={S.statSub}>of {fmt(totalValueAll)} overall</div>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     v16.2 — EXPORT BY STATUS BAR (Batch Management)
     ═══════════════════════════════════════════ */
  const renderStatusExportBar = () => {
    const statusColor = { rejected: '#cf1322', approved: '#237804', on_hold: HOLD_COLOR, pending: '#d46b08', all: '#1a1a1a' }[exportStatus];
    return (
      <div style={S.panel}>
        <div style={S.panelHeader}>
          <FilterOutlined style={{ color: '#1a5276', fontSize: 12 }} />
          <span style={S.sectionTitle}>Export Invoices by Status</span>
          <span style={{ fontSize: 10, color: '#888' }}>across all batches</span>
        </div>
        <div style={{ ...S.panelBody, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={S.subLabel}>Status</span>
          <Select value={exportStatus} onChange={setExportStatus} size="small" style={{ minWidth: 130, fontSize: 11 }} options={STATUS_FILTER_OPTIONS} />
          <span style={{ ...S.subLabel, marginLeft: 6 }}>Category</span>
          <Select value={exportCategory} onChange={setExportCategory} size="small" style={{ minWidth: 160, fontSize: 11 }}
            options={[{ value: 'all', label: 'All Categories' }, ...Object.entries(CATEGORY_LABELS).map(([k, v]) => ({ value: k, label: v }))]} />
          <span style={{ ...S.subLabel, marginLeft: 6 }}>Batches</span>
          <Select value={exportScope} onChange={setExportScope} size="small" style={{ minWidth: 140, fontSize: 11 }}
            options={[{ value: 'all', label: 'All Batches' }, { value: 'received', label: 'Received only' }, { value: 'open', label: 'Open only' }]} />
          {exportPreviewCount !== null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: exportPreviewCount > 0 ? statusColor : '#bfbfbf', marginLeft: 6 }}>
              {exportPreviewCount} invoice{exportPreviewCount !== 1 ? 's' : ''}
            </span>
          )}
          <Button size="small" type="primary" icon={exportingStatus ? <LoadingOutlined spin /> : <FileExcelOutlined />}
            onClick={handleExportByStatus} disabled={exportingStatus || exportPreviewCount === 0}
            style={{ ...S.plainBtn, fontWeight: 600, marginLeft: 'auto', background: '#237804', borderColor: '#237804' }}>
            Export Excel
          </Button>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     BATCHES TAB
     ═══════════════════════════════════════════ */
  const renderBatchesTab = () => {
    const batchCols = [
      { title: 'Batch', dataIndex: 'batch_no', key: 'bn', width: 80, sorter: (a,b) => a.batch_no - b.batch_no, render: v => <Button type="link" onClick={() => openBatchFresh(v)} style={{ fontWeight: 700, fontSize: 13, padding: 0, color: '#1a5276' }}>{v}</Button> },
      { title: 'Status', dataIndex: 'batch_status', key: 'bs', width: 100, filters: [{text:'Open',value:'open'},{text:'Received',value:'received'}], onFilter: (v,r) => (r.batch_status||'open') === v, render: s => s === 'received' ? <Tag icon={<LockOutlined />} style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#cf1322', background: '#fff1f0', border: '1px solid #ffa39e' }}>Received</Tag> : <Tag icon={<UnlockOutlined />} style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#237804', background: '#f6ffed', border: '1px solid #b7eb8f' }}>Open</Tag> },
      { title: 'Invoices', dataIndex: 'total_submissions', key: 'ti', width: 75, align: 'center', sorter: (a,b) => a.total_submissions - b.total_submissions, render: v => <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{v}</span> },
      { title: 'Pending', dataIndex: 'pending_count', key: 'pc', width: 70, align: 'center', render: v => <span style={{ fontSize: 12, color: v > 0 ? '#d46b08' : '#d9d9d9', fontWeight: v > 0 ? 700 : 400 }}>{v}</span> },
      { title: 'On Hold', dataIndex: 'on_hold_count', key: 'hc', width: 70, align: 'center', render: v => <span style={{ fontSize: 12, color: v > 0 ? HOLD_COLOR : '#d9d9d9', fontWeight: v > 0 ? 700 : 400 }}>{v || 0}</span> },
      { title: 'Approved', dataIndex: 'approved_count', key: 'ac', width: 75, align: 'center', render: v => <span style={{ fontSize: 12, color: v > 0 ? '#237804' : '#d9d9d9', fontWeight: v > 0 ? 700 : 400 }}>{v}</span> },
      { title: 'Rejected', dataIndex: 'rejected_count', key: 'rc', width: 70, align: 'center', render: v => <span style={{ fontSize: 12, color: v > 0 ? '#cf1322' : '#d9d9d9', fontWeight: v > 0 ? 700 : 400 }}>{v}</span> },
      { title: 'Total Value', dataIndex: 'total_value', key: 'tv', width: 135, align: 'right', sorter: (a,b) => a.total_value - b.total_value, render: v => <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{fmt(v)}</span> },
      { title: 'Last Upload', dataIndex: 'last_submission', key: 'ls', width: 90, render: v => <span style={{ fontSize: 11, color: '#888' }}>{fmtDate(v)}</span> },
      { title: 'Received By', dataIndex: 'received_by', key: 'rb', width: 105, render: v => v ? <span style={{ fontSize: 11, color: '#555' }}>{v.split('@')[0]}</span> : <span style={{ color: '#d9d9d9' }}>—</span> },
      { title: 'Action', key: 'action', width: 360, fixed: 'right', render: (_,rec) => (
  <Space size={4}>
    <Button size="small" onClick={() => openBatchFresh(rec.batch_no)} style={S.plainBtn}>View</Button>
    <Tooltip title="PDF Report">
      <Button size="small"
        icon={downloadingPdf === rec.batch_no ? <LoadingOutlined spin /> : <FilePdfOutlined />}
        onClick={() => handleDownloadPdf(rec.batch_no)}
        loading={downloadingPdf === rec.batch_no}
        disabled={rec.total_submissions === 0}
        style={{ ...S.plainBtn, color: '#cf1322', borderColor: '#ffa39e' }}>PDF</Button>
    </Tooltip>
    <Tooltip title="Excel Report">
      <Button size="small"
        icon={downloadingExcel === rec.batch_no ? <LoadingOutlined spin /> : <FileExcelOutlined />}
        onClick={() => handleDownloadExcel(rec.batch_no)}
        loading={downloadingExcel === rec.batch_no}
        disabled={rec.total_submissions === 0}
        style={{ ...S.plainBtn, color: '#237804', borderColor: '#b7eb8f' }}>Excel</Button>
    </Tooltip>
    <BatchPdfDownloadDropdown batchNo={rec.batch_no} compact />
    {(rec.batch_status||'open') !== 'received'
      ? <Popconfirm title={`Mark Batch ${rec.batch_no} as received?`} description="Blocks uploads." onConfirm={() => markBatchReceived(rec.batch_no)} okText="Confirm">
          <Button size="small" type="primary" style={{ ...S.plainBtn, background: '#237804', borderColor: '#237804' }}>Receive</Button>
        </Popconfirm>
      : <Popconfirm title={`Reopen Batch ${rec.batch_no}?`} onConfirm={() => reopenBatch(rec.batch_no)} okText="Reopen">
          <Button size="small" style={S.plainBtn}>Reopen</Button>
        </Popconfirm>}
  </Space>
)},
    ];
    return (<div>
      {renderFolderBar()}
      {renderStats()}

      <div style={S.statRow}>
        {[
          { label: 'Batches with Data', value: totalBatches, icon: <FolderOpenOutlined /> },
          { label: 'Open',     value: openBatches,     color: '#237804', icon: <UnlockOutlined /> },
          { label: 'Received', value: receivedBatches, color: '#cf1322', icon: <LockOutlined /> },
          { label: 'Total Invoices', value: totalInvoices, color: '#1a5276', icon: <FileTextOutlined /> },
          { label: 'On Hold (All)', value: stats?.on_hold || 0, color: HOLD_COLOR, icon: <PauseCircleOutlined /> },
          { label: 'Total Value',    value: fmt(totalValueAll), isText: true, color: '#1a5276', icon: <DollarOutlined /> },
        ].map((item, idx, arr) => (
          <div key={idx} style={S.statCell(idx === arr.length - 1)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
              <span style={{ color: item.color || '#888', fontSize: 11 }}>{item.icon}</span>
              <span style={S.statLabel}>{item.label}</span>
            </div>
            <div style={{ ...S.statValue(item.color), fontSize: item.isText ? 13 : 17 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {renderStatusExportBar()}

      <div style={S.panel}>
        <div style={S.panelHeader}>
          <Input placeholder="Filter batch..." value={batchSearch} onChange={e => setBatchSearch(e.target.value)} allowClear prefix={<NumberOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 3, maxWidth: 180, height: 26, fontSize: 11 }} size="small" />
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchBatchSummaries} loading={batchLoading} style={S.plainBtn}>Refresh</Button>
          <Tooltip title="Export all batches to Excel"><Button size="small" icon={downloadingAllExcel ? <LoadingOutlined spin /> : <FileExcelOutlined />} onClick={handleDownloadAllExcel} loading={downloadingAllExcel} style={{ ...S.plainBtn, color: '#237804', borderColor: '#b7eb8f' }}>Export All</Button></Tooltip>
          <BulkPdfDownloadButtons />
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#888' }}>{filteredBatches.length} batches</span>
        </div>
        {batchLoading ? <div style={{ padding: 50, textAlign: 'center' }}><Spin /></div> :
          filteredBatches.length === 0 ? <Empty style={{ padding: 40 }} description="No batches" image={Empty.PRESENTED_IMAGE_SIMPLE} /> :
          <Table columns={batchCols} dataSource={filteredBatches.map(b => ({ ...b, key: b.batch_no }))} pagination={filteredBatches.length > 20 ? { pageSize: 20, size: 'small', showTotal: t => `${t} batches` } : false} size="small" bordered scroll={{ x: 1350 }} className="pts-table" />
        }
      </div>
    </div>);
  };

  /* ═══════════════════════════════════════════
     ALL SUBMISSIONS / PENDING / ON HOLD TAB (plain)
     ═══════════════════════════════════════════ */
  const renderAllTab = () => (<div>
    {renderFolderBar()}
    {renderStats()}
    {allStatusFilter === 'on_hold' && (
      <Alert type="warning" showIcon icon={<PauseCircleOutlined />}
        message="Invoices On Hold by Accounts"
        description="Each of these shows the uploader a note naming the supplier and invoice number. Open one to release the hold, approve or reject it — or tick several and use bulk approve / reject."
        style={{ borderRadius: 3, marginBottom: 12, fontSize: 12 }} />
    )}
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <Input placeholder="Search..." value={allSearchText} onChange={e => setAllSearchText(e.target.value)} allowClear prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 3, maxWidth: 240, height: 26, fontSize: 11 }} size="small" />
        <Select value={allStatusFilter} onChange={v => { setAllStatusFilter(v); setAllPage(1); clearSelection(); }} size="small" style={{ minWidth: 130, fontSize: 11 }} options={[{value:'all',label:'All Statuses'},{value:'pending_review',label:'Pending Review'},{value:'on_hold',label:'On Hold'},{value:'approved',label:'Approved'},{value:'rejected',label:'Rejected'}]} />
        <Select value={allCategoryFilter} onChange={v => { setAllCategoryFilter(v); setAllPage(1); clearSelection(); }} size="small" style={{ minWidth: 150, fontSize: 11 }} options={[{value:'all',label:'All Categories'},...Object.entries(CATEGORY_LABELS).map(([k,v])=>({value:k,label:v}))]} />
        {selectedIds.length > 0 && <Tag style={{ borderRadius: 3, fontSize: 11, margin: 0, padding: '0 6px', lineHeight: '20px', color: '#fff', background: '#1a1a1a', border: '1px solid #1a1a1a' }}>{selectedIds.length} selected</Tag>}
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchAllSubmissions} loading={allLoading} style={{ ...S.plainBtn, marginLeft: 'auto' }}>Refresh</Button>
      </div>
      {allLoading ? <div style={{ padding: 50, textAlign: 'center' }}><Spin /></div> :
        filteredAllSubs.length === 0 ? <Empty style={{ padding: 40 }} image={Empty.PRESENTED_IMAGE_SIMPLE} /> :
        <Table rowSelection={rowSelection} columns={makeSubColumns({ plain: true })} dataSource={filteredAllSubs.map(s => ({ ...s, key: s.id }))} rowClassName={rowClass} pagination={{ current: allPage, pageSize: allPageSize, total: allTotal, onChange: (p,ps) => { setAllPage(p); setAllPageSize(ps); }, showSizeChanger: true, size: 'small', showTotal: t => `${t} total` }} size="small" bordered scroll={{ x: showExtraCols ? 2084 : 1214 }} className="pts-table pts-plain" />
      }
    </div>
  </div>);

  /* ═══════════════════════════════════════════
     BATCH DRAWER
     ═══════════════════════════════════════════ */
  const renderBatchDrawer = () => {
    if (!selectedBatch) return null;  
    const isReceived = batchInfo?.batch_status === 'received';
    const bTotal = batchSubmissions.reduce((s,b) => s + (Number(b.total_amount)||0), 0);
    const bPending = batchSubmissions.filter(s => s.status === 'pending_review' || s.status === 'under_review').length;
    const bHold = batchSubmissions.filter(s => s.status === 'on_hold').length;
    const bApproved = batchSubmissions.filter(s => s.status === 'approved').length;
    const bRejected = batchSubmissions.filter(s => s.status === 'rejected').length;
    const filteredValue = filteredBatchSubs.reduce((s, r) => s + (Number(r.total_amount) || 0), 0);
    const statusBtnColor = { all: '#000', pending: '#d46b08', on_hold: HOLD_COLOR, approved: '#237804', rejected: '#cf1322' };
    return (<Drawer
      title={<div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_FAMILY }}>
        <div style={{ width: 30, height: 30, borderRadius: 3, background: '#f5f5f5', border: '1px solid #d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a5276' }}><NumberOutlined style={{ fontSize: 13 }} /></div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>Batch {selectedBatch}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{batchSubmissions.length} invoices · {fmt(bTotal)}</div>
        </div>
        {isReceived ? <Tag icon={<LockOutlined />} style={{ borderRadius: 3, fontSize: 10, margin: '0 0 0 6px', padding: '0 5px', lineHeight: '18px', color: '#cf1322', background: '#fff1f0', border: '1px solid #ffa39e' }}>Received</Tag> : <Tag icon={<UnlockOutlined />} style={{ borderRadius: 3, fontSize: 10, margin: '0 0 0 6px', padding: '0 5px', lineHeight: '18px', color: '#237804', background: '#f6ffed', border: '1px solid #b7eb8f' }}>Open</Tag>}
        {bHold > 0 && <Tag icon={<PauseCircleOutlined />} style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: HOLD_COLOR, background: '#fff7e6', border: '1px solid #ffd591' }}>{bHold} on hold</Tag>}
      </div>}
      placement="right" open={!!selectedBatch} onClose={closeBatch} width="92vw" styles={{ body: { padding: 0, fontFamily: FONT_FAMILY } }} destroyOnClose
      extra={<Space>
  <Tooltip title="PDF Report">
    <Button size="small"
      icon={downloadingPdf === selectedBatch ? <LoadingOutlined spin /> : <FilePdfOutlined />}
      onClick={() => handleDownloadPdf(selectedBatch)}
      loading={downloadingPdf === selectedBatch}
      disabled={batchSubmissions.length === 0}
      style={{ ...S.plainBtn, color: '#cf1322', borderColor: '#ffa39e' }}>PDF</Button>
  </Tooltip>
  <Tooltip title="Excel Report">
    <Button size="small"
      icon={downloadingExcel === selectedBatch ? <LoadingOutlined spin /> : <FileExcelOutlined />}
      onClick={() => handleDownloadExcel(selectedBatch)}
      loading={downloadingExcel === selectedBatch}
      disabled={batchSubmissions.length === 0}
      style={{ ...S.plainBtn, color: '#237804', borderColor: '#b7eb8f' }}>Excel</Button>
  </Tooltip>
  <BatchPdfDownloadDropdown batchNo={selectedBatch} />
  {!isReceived
    ? <Popconfirm title={`Mark Batch ${selectedBatch} received?`} onConfirm={() => markBatchReceived(selectedBatch)} okText="Confirm">
        <Button type="primary" size="small" style={{ ...S.plainBtn, background: '#237804', borderColor: '#237804' }}>Mark Received</Button>
      </Popconfirm>
    : <Popconfirm title={`Reopen Batch ${selectedBatch}?`} onConfirm={() => reopenBatch(selectedBatch)} okText="Reopen">
        <Button size="small" style={S.plainBtn}>Reopen</Button>
      </Popconfirm>}
</Space>}>
      {batchDetailLoading ? <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div> : (<div>
        {isReceived && batchInfo?.batch_received_by && <Alert type="info" showIcon icon={<LockOutlined />} message={`Received by ${batchInfo.batch_received_by.split('@')[0]} on ${fmtDateTime(batchInfo.batch_received_at)}`} style={{ margin: '12px 14px 0', borderRadius: 3, fontSize: 12 }} />}
        {bHold > 0 && <Alert type="warning" showIcon icon={<PauseCircleOutlined />} message={`${bHold} invoice${bHold > 1 ? 's are' : ' is'} on hold in this batch — the uploader has been notified in the log sheet`} style={{ margin: '10px 14px 0', borderRadius: 3, fontSize: 12 }} />}
        {rootDirHandle && <div style={{ margin: '10px 14px 0', padding: '5px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 6 }}><LinkOutlined style={{ color: '#237804', fontSize: 11 }} /><span style={{ fontSize: 11, color: '#237804' }}>Save buttons write to <strong>{rootDirName}/Batch-{selectedBatch}/</strong></span></div>}

        <div style={{ ...S.statRow, margin: '12px 14px' }}>
          {[{l:'Total',v:batchSubmissions.length,c:'#1a1a1a'},{l:'Pending',v:bPending,c:'#d46b08'},{l:'On Hold',v:bHold,c:HOLD_COLOR},{l:'Approved',v:bApproved,c:'#237804'},{l:'Rejected',v:bRejected,c:'#cf1322'},{l:'Value',v:fmt(bTotal),c:'#1a5276',t:true}].map((i,x,a)=>(
            <div key={x} style={S.statCell(x === a.length - 1)}>
              <div style={S.statLabel}>{i.l}</div>
              <div style={{ ...S.statValue(i.c), fontSize: i.t ? 13 : 16 }}>{i.v}</div>
            </div>
          ))}
        </div>

        {batchCategoryBreakdown.length > 0 && (
          <div style={{ padding: '0 14px 8px' }}>
            <div style={{ ...S.subLabel, marginBottom: 6, color: '#000' }}>Filter by Category</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Button size="small" onClick={() => setBatchCatFilter('all')}
                style={{ borderRadius: 3, fontSize: 11, fontWeight: batchCatFilter === 'all' ? 700 : 600,
                  color: batchCatFilter === 'all' ? '#fff' : '#000',
                  background: batchCatFilter === 'all' ? '#000' : '#fff',
                  borderColor: batchCatFilter === 'all' ? '#000' : '#d9d9d9' }}>
                All ({batchSubmissions.length})
              </Button>
              {batchCategoryBreakdown.map(cat => {
                const active = batchCatFilter === cat.category;
                return (
                  <Button key={cat.category} size="small" onClick={() => setBatchCatFilter(cat.category)}
                    style={{ borderRadius: 3, fontSize: 11, fontWeight: active ? 700 : 600,
                      color: active ? '#fff' : '#000',
                      background: active ? '#000' : '#fff',
                      borderColor: active ? '#000' : '#d9d9d9' }}>
                    {CATEGORY_LABELS[cat.category] || cat.category} ({cat.items.length}{cat.pending > 0 ? ` · ${cat.pending} pending` : ''}{cat.onHold > 0 ? ` · ${cat.onHold} on hold` : ''})
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* v16.2 — status filter + export filtered */}
        {batchSubmissions.length > 0 && (
          <div style={{ padding: '0 14px 10px' }}>
            <div style={{ ...S.subLabel, marginBottom: 6, color: '#000' }}>Filter by Status</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {STATUS_FILTER_OPTIONS.map(o => {
                const active = batchStatusFilter === o.value;
                const c = statusBtnColor[o.value];
                const n = batchStatusCounts[o.value] || 0;
                return (
                  <Button key={o.value} size="small" onClick={() => setBatchStatusFilter(o.value)}
                    disabled={o.value !== 'all' && n === 0}
                    style={{ borderRadius: 3, fontSize: 11, fontWeight: active ? 700 : 600,
                      color: active ? '#fff' : c,
                      background: active ? c : '#fff',
                      borderColor: active ? c : '#d9d9d9' }}>
                    {o.value === 'all' ? 'All' : o.label} ({n})
                  </Button>
                );
              })}
              <span style={{ fontSize: 11, color: '#555', marginLeft: 8 }}>
                Showing <b>{filteredBatchSubs.length}</b> · {fmt(filteredValue)}
              </span>
              <Tooltip title="Download exactly the rows shown below as .xlsx">
                <Button size="small" icon={<FileExcelOutlined />} onClick={handleExportBatchFiltered}
                  disabled={!filteredBatchSubs.length}
                  style={{ ...S.plainBtn, fontWeight: 600, marginLeft: 'auto', color: '#237804', borderColor: '#b7eb8f' }}>
                  Export Filtered ({filteredBatchSubs.length})
                </Button>
              </Tooltip>
            </div>
          </div>
        )}

        <div style={{ padding: '0 14px 14px' }}>
          {filteredBatchSubs.length === 0
            ? <Empty style={{ padding: 30 }} image={Empty.PRESENTED_IMAGE_SIMPLE} description="No invoices for this filter" />
            : <Table rowSelection={rowSelection} columns={makeSubColumns({ showBatch: false, plain: true })} dataSource={filteredBatchSubs.map(s => ({ ...s, key: s.id }))} rowClassName={rowClass} pagination={filteredBatchSubs.length > 25 ? { pageSize: 25, size: 'small' } : false} size="small" bordered scroll={{ x: showExtraCols ? 1934 : 1034 }} className="pts-table pts-plain" />}
        </div>
      </div>)}
    </Drawer>);
  };

  /* ═══════════════════════════════════════════
     DETAIL DRAWER
     ═══════════════════════════════════════════ */
  const renderDetailDrawer = () => {
    if (!detailData) return null;
    const isOnHold = detailData.status === 'on_hold';
    const canReview = ['pending_review', 'under_review', 'on_hold'].includes(detailData.status);
    const canOverride = detailData.status === 'approved' || detailData.status === 'rejected';
    const revisions = detailData.revisions || [];
    return (<Drawer
      title={<div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontFamily: FONT_FAMILY }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>{detailData.submission_ref}</span>
        {statusTag(detailData.status)}
        <span style={{ fontSize: 11, color: '#888' }}>{CATEGORY_LABELS[detailData.category] || detailData.category}</span>
        {detailData.batch_no && <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>Batch {detailData.batch_no}</Tag>}
        {detailData.revision_count > 0 && <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#531dab', background: '#f9f0ff', border: '1px solid #d3adf7' }}>Rev #{detailData.revision_count}</Tag>}
      </div>}
      placement="right" open={!!detailId} onClose={closeDetail} width={700}
      styles={{ body: { padding: 0, overflowY: 'auto', fontFamily: FONT_FAMILY } }} destroyOnClose
      extra={<Space size={6}>
        <Button size="small" icon={<PrinterOutlined />} onClick={handlePrintInvoice} style={S.plainBtn}>Print</Button>
        <Button size="small" onClick={() => openPreview(detailId)} style={S.plainBtn}>Preview Docs</Button>
      </Space>}>
      <style>{`.edit-field-btn { opacity: 0 !important; transition: opacity 0.15s; } .ant-descriptions-item-content:hover .edit-field-btn { opacity: 1 !important; }`}</style>
      <div>
        {/* v15 — hold banner */}
        {isOnHold && (
          <Alert type="warning" showIcon icon={<PauseCircleOutlined />}
            message={<span style={{ fontWeight: 700 }}>On hold with Accounts</span>}
            description={
              <div>
                <div style={{ fontSize: 12 }}>{detailData.hold_remarks || detailData.accounts_remarks}</div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
                  Held by {(detailData.held_by || '').split('@')[0] || '—'} · {fmtDateTime(detailData.held_at)} — this is exactly what the uploader sees.
                </div>
              </div>
            }
            style={{ margin: '14px 18px 0', borderRadius: 3 }} />
        )}

        {!isOnHold && detailData.accounts_remarks && <Alert type={detailData.status === 'approved' ? 'success' : detailData.status === 'rejected' ? 'error' : 'info'} showIcon={false} message={`${detailData.status === 'approved' ? 'Approved' : detailData.status === 'rejected' ? 'Rejected' : 'Remarks'} by ${detailData.reviewed_by || 'accounts'}`} description={<div><div style={{ fontSize: 12 }}>{detailData.accounts_remarks}</div><div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{fmtDateTime(detailData.reviewed_at)}</div></div>} style={{ margin: '14px 18px 0', borderRadius: 3 }} />}

        <div style={{ padding: '14px 18px' }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 8px' }}>Invoice Details</h4>
          <Descriptions bordered size="small" column={1} labelStyle={{ background: '#fafafa', fontWeight: 500, width: 130, color: '#555', fontSize: 11 }} contentStyle={{ fontSize: 12 }}>
            <Descriptions.Item label="Batch">{detailData.batch_no ? <Tag style={{ borderRadius: 3, fontSize: 11, fontWeight: 700, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>{detailData.batch_no}</Tag> : '-'}</Descriptions.Item>
            <Descriptions.Item label="LPO Number">{renderEditableField('lpo_number', detailData.lpo_number, { isBold: true })}</Descriptions.Item>
            <Descriptions.Item label="Vendor">{renderEditableField('vendor_name', detailData.vendor_name, { isBold: true })}</Descriptions.Item>
            <Descriptions.Item label="Invoice No.">{renderEditableField('invoice_number', detailData.invoice_number)}</Descriptions.Item>
            <Descriptions.Item label="Invoice Date">{renderEditableField('invoice_date', detailData.invoice_date)}</Descriptions.Item>
            <Descriptions.Item label="Description">{renderEditableField('description', detailData.description)}</Descriptions.Item>
            <Descriptions.Item label="Project Code">{renderEditableField('project_code', detailData.project_code)}</Descriptions.Item>
            <Descriptions.Item label="Vendor TRN">{renderEditableField('vendor_trn', detailData.vendor_trn)}</Descriptions.Item>
            <Descriptions.Item label="Vendor Email">{detailData.vendor_email || '-'}</Descriptions.Item>
            <Descriptions.Item label="Vendor Phone">{detailData.vendor_phone || '-'}</Descriptions.Item>
          </Descriptions>
        </div>

        <div style={{ padding: '0 18px 14px' }}>
          <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 3, padding: '10px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span style={{ fontSize: 11, color: '#666' }}>Subtotal</span>{renderEditableField('subtotal_amount', detailData.subtotal_amount, { isAmount: true })}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span style={{ fontSize: 11, color: '#666' }}>VAT ({detailData.vat_percentage || 5}%)</span>{renderEditableField('vat_amount', detailData.vat_amount, { isAmount: true })}</div>
            <Divider style={{ margin: '6px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 13, fontWeight: 600 }}>Total</span>{renderEditableField('total_amount', detailData.total_amount, { isAmount: true, isBold: true })}</div>
          </div>
        </div>

        {(detailData.dn_number || detailData.dn_date || detailData.dn_received_by) && <div style={{ padding: '0 18px 14px' }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 8px' }}>Delivery Note</h4>
          <Descriptions bordered size="small" column={2} labelStyle={{ background: '#fafafa', fontWeight: 500, color: '#555', fontSize: 11 }} contentStyle={{ fontSize: 11 }}>
            <Descriptions.Item label="DN Number">{renderEditableField('dn_number', detailData.dn_number)}</Descriptions.Item>
            <Descriptions.Item label="DN Date">{renderEditableField('dn_date', detailData.dn_date)}</Descriptions.Item>
            <Descriptions.Item label="Received By">{renderEditableField('dn_received_by', detailData.dn_received_by)}</Descriptions.Item>
            <Descriptions.Item label="DN Project">{renderEditableField('dn_project_code', detailData.dn_project_code)}</Descriptions.Item>
          </Descriptions>
        </div>}

        <div style={{ padding: '0 18px 14px' }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 6px' }}>Documents ({(detailData.documents||[]).length})</h4>
          {(detailData.documents||[]).length === 0 ? <div style={{ color: '#999', fontSize: 11, padding: '10px 0' }}>No documents</div> : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ borderBottom: '1px solid #e8e8e8' }}><th style={{ textAlign: 'left', padding: '5px 0', color: '#888', fontWeight: 500, fontSize: 10 }}>Type</th><th style={{ textAlign: 'left', padding: '5px 0', color: '#888', fontWeight: 500, fontSize: 10 }}>Filename</th><th style={{ textAlign: 'right', padding: '5px 0', color: '#888', fontWeight: 500, fontSize: 10 }}>Action</th></tr></thead>
            <tbody>{(detailData.documents||[]).map((doc, idx) => (<tr key={doc.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
              <td style={{ padding: '5px 0', color: '#333' }}>{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}{doc.revision_id && <span style={{ color: '#531dab', fontSize: 9, marginLeft: 4 }}>Rev</span>}</td>
              <td style={{ padding: '5px 0', color: '#555', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.renamed_filename || doc.original_filename}</td>
              <td style={{ padding: '5px 0', textAlign: 'right' }}><Space size={6}>
                <a onClick={() => openPreview(detailId, idx)} style={{ color: '#1a5276', fontSize: 11 }}>View</a>
                <a onClick={() => handleSaveDocFile(doc.id, detailData.batch_no)} style={{ color: '#237804', fontSize: 11 }}>{savingDocId === doc.id ? <LoadingOutlined spin /> : <><SaveOutlined /> {rootDirHandle ? 'Save' : 'Download'}</>}</a>
              </Space></td>
            </tr>))}</tbody>
          </table>}
        </div>

        {revisions.length > 0 && <div style={{ padding: '0 18px 14px' }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 6px' }}>Revisions ({revisions.length})</h4>
          {revisions.map(rev => (<div key={rev.id} style={{ padding: '6px 10px', border: '1px solid #e8e8e8', borderRadius: 3, marginBottom: 5, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}><div><span style={{ fontWeight: 600, color: '#531dab', fontSize: 11, marginRight: 5 }}>Rev {rev.revision_number}</span>{statusTag(rev.status)}</div><span style={{ fontSize: 10, color: '#888' }}>{fmtDateTime(rev.submitted_at)}</span></div>
            <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#555' }}><span>Invoice: {rev.invoice_number || '-'}</span><span>Total: <strong>{fmt(rev.total_amount)}</strong></span></div>
            {rev.accounts_remarks && <div style={{ marginTop: 3, padding: '3px 6px', background: '#fff1f0', borderRadius: 3, fontSize: 10, color: '#cf1322' }}>{rev.accounts_remarks}</div>}
          </div>))}
        </div>}

        <div style={{ padding: '0 18px 14px' }}>
          <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 3, padding: '6px 10px', fontSize: 11 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}><span style={{ color: '#888' }}>Submitted By</span><span>{detailData.submitted_by}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}><span style={{ color: '#888' }}>Submitted</span><span>{fmtDateTime(detailData.submitted_at)}</span></div>
            {isOnHold && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}><span style={{ color: '#888' }}>Held Since</span><span style={{ color: HOLD_COLOR, fontWeight: 600 }}>{fmtDateTime(detailData.held_at)}</span></div>}
            {detailData.revision_count > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#888' }}>Revisions</span><span style={{ color: '#531dab', fontWeight: 500 }}>{detailData.revision_count}</span></div>}
          </div>
        </div>

        {detailData.audit_logs?.length > 0 && <div style={{ padding: '0 18px 14px' }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 6px' }}>Audit Trail</h4>
          <div style={{ fontSize: 11 }}>{detailData.audit_logs.map((log,i)=>(<div key={log.id} style={{ padding: '5px 0', borderBottom: i < detailData.audit_logs.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span><strong style={{ color: log.action === 'held' ? HOLD_COLOR : '#333' }}>{AUDIT_LABELS[log.action] || log.action}</strong><span style={{ color: '#888', marginLeft: 5 }}>by {log.performed_by}</span></span><span style={{ color: '#aaa', fontSize: 10 }}>{fmtDateTime(log.performed_at)}</span></div>
            {log.details?.from && log.details?.to && <div style={{ marginTop: 2, color: '#666' }}>{STATUS_CFG[log.details.from]?.label || log.details.from} → {STATUS_CFG[log.details.to]?.label || log.details.to}</div>}
            {log.details?.manual_note && <div style={{ marginTop: 2, color: '#666' }}>Note: {log.details.manual_note}</div>}
            {log.details?.remarks && <div style={{ marginTop: 2, color: '#666' }}>{log.details.remarks}</div>}
          </div>))}</div>
        </div>}

        {canReview && <div style={{ padding: '12px 18px', background: '#fafafa', borderTop: '1px solid #e8e8e8', position: 'sticky', bottom: 0 }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 6px' }}>{isOnHold ? 'On Hold — Decide Or Release' : 'Review Decision'}</h4>
          <TextArea rows={2} placeholder="Review remarks (required for rejection)..." value={reviewRemarks} onChange={e => setReviewRemarks(e.target.value)} style={{ borderRadius: 3, marginBottom: 6, fontSize: 12 }} />
          <TextArea rows={1} placeholder={isOnHold ? 'Optional note when releasing the hold...' : 'Optional hold note — appended to the auto message'} value={holdRemarks} onChange={e => setHoldRemarks(e.target.value)} style={{ borderRadius: 3, marginBottom: 5, fontSize: 12 }} />
          {!isOnHold && (
            <div style={{ fontSize: 10, color: '#888', marginBottom: 8, lineHeight: 1.5 }}>
              Hold sends the uploader: <span style={{ color: HOLD_COLOR }}>{holdPreviewText(holdRemarks)}</span>
            </div>
          )}
          <Space size={8} wrap>
            <Button type="primary" loading={reviewLoading} onClick={() => handleReview('approved')} style={{ ...S.plainBtn, fontWeight: 600, background: '#237804', borderColor: '#237804', height: 30 }}>Approve</Button>
            <Button danger loading={reviewLoading} onClick={() => handleReview('rejected')} style={{ ...S.plainBtn, fontWeight: 600, height: 30 }}>Reject</Button>
            {!isOnHold
              ? <Button icon={<PauseCircleOutlined />} loading={holdLoading} onClick={handleHold} style={{ ...S.holdBtn, fontWeight: 600, height: 30 }}>Put On Hold</Button>
              : <Button icon={<PlayCircleOutlined />} loading={holdLoading} onClick={handleReleaseHold} style={{ ...S.plainBtn, fontWeight: 600, height: 30 }}>Release Hold</Button>}
          </Space>
        </div>}

        {canOverride && <div style={{ padding: '12px 18px', background: '#fafafa', borderTop: '1px solid #e8e8e8', position: 'sticky', bottom: 0 }}>
          <h4 style={{ ...S.subLabel, margin: '0 0 6px' }}>Change Status (Override)</h4>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            Current: {statusTag(detailData.status)} — override the existing decision.
          </div>
          <TextArea rows={2} placeholder="Remarks (required when rejecting)..." value={statusRemarks} onChange={e => setStatusRemarks(e.target.value)} style={{ borderRadius: 3, marginBottom: 6, fontSize: 12 }} />
          <TextArea rows={1} placeholder="Optional hold note — appended to the auto message" value={holdRemarks} onChange={e => setHoldRemarks(e.target.value)} style={{ borderRadius: 3, marginBottom: 8, fontSize: 12 }} />
          <Space size={8} wrap>
            {detailData.status !== 'approved' && (
              <Button type="primary" loading={statusChangeLoading} onClick={() => handleChangeStatus('approved')} style={{ ...S.plainBtn, fontWeight: 600, background: '#237804', borderColor: '#237804', height: 30 }}>Mark Approved</Button>
            )}
            {detailData.status !== 'rejected' && (
              <Button danger loading={statusChangeLoading} onClick={() => handleChangeStatus('rejected')} style={{ ...S.plainBtn, fontWeight: 600, height: 30 }}>Mark Rejected</Button>
            )}
            <Button icon={<PauseCircleOutlined />} loading={holdLoading} onClick={handleHold} style={{ ...S.holdBtn, fontWeight: 600, height: 30 }}>Put On Hold</Button>
            <Button loading={statusChangeLoading} onClick={() => handleChangeStatus('pending_review')} style={{ ...S.plainBtn, height: 30 }}>Reopen for Review</Button>
          </Space>
        </div>}
      </div>
    </Drawer>);
  };

  /* ═══════════════════════════════════════════
     DOC PREVIEW DRAWER
     ═══════════════════════════════════════════ */
  const renderDocPreview = () => {
    const doc = previewDocs[activeDocIdx];
    return (<Drawer
      title={<div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT_FAMILY }}><span style={{ fontWeight: 600, fontSize: 13 }}>Document Preview</span>{previewDocs.length > 0 && <span style={{ fontSize: 11, color: '#888' }}>{activeDocIdx+1} of {previewDocs.length}</span>}</div>}
      placement="right" open={previewVisible} onClose={() => setPreviewVisible(false)} width="80vw" styles={{ body: { padding: 0, overflow: 'hidden' } }} destroyOnClose>
      {previewLoading ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Spin /></div> : !previewDocs.length ? <Empty style={{ marginTop: 60 }} /> : <div style={{ display: 'flex', height: 'calc(100vh - 48px)', overflow: 'hidden' }}>
        <div style={{ width: 200, minWidth: 200, borderRight: '1px solid #e8e8e8', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e8e8e8', background: '#fff' }}><span style={{ fontSize: 11, fontWeight: 600 }}>Documents</span></div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 5 }}>{previewDocs.map((d,i)=>{const active=i===activeDocIdx;return(<div key={d.id} onClick={()=>setActiveDocIdx(i)} style={{ padding: '6px 9px', borderRadius: 3, cursor: 'pointer', marginBottom: 2, background: active ? '#1a5276' : 'transparent' }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: active?'#fff':'#333' }}>{DOC_TYPE_LABELS[d.doc_type]||d.doc_type}</div>
            {d.revision_id&&<span style={{ fontSize: 9, color: active?'rgba(255,255,255,0.7)':'#531dab' }}>Revised</span>}
            <div style={{ fontSize: 9, color: active?'rgba(255,255,255,0.6)':'#aaa', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', marginTop: 2 }}>{d.filename}</div>
          </div>);})}</div>
          <div style={{ padding: '6px 10px', borderTop: '1px solid #e8e8e8', background: '#fff' }}>
            <Button block size="small" icon={<DownloadOutlined />} onClick={() => doc && downloadDoc(doc)} style={{ ...S.plainBtn, marginBottom: 4 }}>Download</Button>
            <Button block size="small" icon={<SaveOutlined />} onClick={() => doc && handleSaveDocFile(doc.id, detailData?.batch_no)} loading={savingDocId === doc?.id} style={{ ...S.plainBtn, color: rootDirHandle ? '#1a5276' : '#237804', borderColor: rootDirHandle ? '#91caff' : '#b7eb8f' }}>{rootDirHandle ? `Save to ${rootDirName}` : 'Save'}</Button>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ padding: '5px 12px', borderBottom: '1px solid #e8e8e8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Space size={5}>
              <Button size="small" disabled={activeDocIdx===0} onClick={()=>setActiveDocIdx(activeDocIdx-1)} icon={<LeftOutlined />} style={S.plainBtn} />
              <span style={{ fontSize: 11, color: '#666' }}>{activeDocIdx+1}/{previewDocs.length}</span>
              <Button size="small" disabled={activeDocIdx===previewDocs.length-1} onClick={()=>setActiveDocIdx(activeDocIdx+1)} icon={<RightOutlined />} style={S.plainBtn} />
            </Space>
            <Space size={5}>
              <Button size="small" onClick={() => doc?.url && window.open(doc.url,'_blank')} style={S.plainBtn}>New Tab</Button>
              <Button size="small" onClick={() => doc && downloadDoc(doc)} icon={<DownloadOutlined />} style={S.plainBtn}>Download</Button>
            </Space>
          </div>
          <div style={{ flex: 1, minHeight: 0, background: '#525659' }}>{doc?.url?(()=>{const ft=(doc.file_type||'').toLowerCase();if(['jpg','jpeg','png','gif','tiff','tif'].includes(ft))return<div style={{ height:'100%', display:'flex', justifyContent:'center', alignItems:'center', overflow:'auto', padding: 12 }}><img src={doc.url} alt={doc.filename} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} /></div>;return<iframe src={`${doc.url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`} title={doc.filename} style={{ width:'100%', height:'100%', border:'none', display:'block' }} />;})():<div style={{ height:'100%', display:'flex', justifyContent:'center', alignItems:'center' }}><Empty description="No preview" /></div>}</div>
          <div style={{ padding: '4px 12px', borderTop: '1px solid #444', background: '#3a3d40', flexShrink: 0, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{doc?.filename}</span>
            <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9 }}>{doc?.file_type?.toUpperCase()}</span>
          </div>
        </div>
      </div>}
    </Drawer>);
  };

  /* ═══════════════════════════════════════════
     v16 — FLOATING BULK ACTION BAR
     Visible whenever invoices are ticked; hidden while a detail / preview /
     bulk modal is open so it never covers their buttons.
     ═══════════════════════════════════════════ */
  const renderBulkBar = () => {
    if (!selectedIds.length || detailId || previewVisible || bulkMode) return null;
    const heldCount = selectedRows.filter(r => r.status === 'on_hold').length;
    const apprCount = selectedRows.filter(r => r.status === 'approved').length;
    const rejCount  = selectedRows.filter(r => r.status === 'rejected').length;
    return (
      <div style={{
        position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1010,
        background: '#1a1a1a', color: '#fff', borderRadius: 4, padding: '8px 10px 8px 16px',
        display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
        fontFamily: FONT_FAMILY, flexWrap: 'wrap', maxWidth: 'calc(100vw - 32px)',
      }}>
        <CheckSquareOutlined style={{ fontSize: 14 }} />
        <span style={{ fontSize: 12, fontWeight: 700 }}>{selectedIds.length} selected</span>
        <span style={{ fontSize: 12, color: '#bfbfbf' }}>{fmt(selectedTotal)}</span>
        {heldCount > 0 && <span style={{ fontSize: 11, color: '#ffc069' }}>{heldCount} on hold</span>}
        {apprCount > 0 && <span style={{ fontSize: 11, color: '#95de64' }}>{apprCount} approved</span>}
        {rejCount > 0 && <span style={{ fontSize: 11, color: '#ff7875' }}>{rejCount} rejected</span>}
        <Space size={6} style={{ marginLeft: 6 }}>
          <Button size="small" icon={<CheckCircleOutlined />} onClick={() => openBulk('approved')}
            style={{ ...S.plainBtn, fontWeight: 600, background: '#237804', borderColor: '#237804', color: '#fff' }}>
            Approve Selected
          </Button>
          <Button size="small" icon={<CloseCircleOutlined />} onClick={() => openBulk('rejected')}
            style={{ ...S.plainBtn, fontWeight: 600, background: '#cf1322', borderColor: '#cf1322', color: '#fff' }}>
            Reject Selected
          </Button>
          <Button size="small" type="text" onClick={clearSelection} style={{ ...S.plainBtn, color: '#bfbfbf' }}>Clear</Button>
        </Space>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     v16 / v16.1 — BULK REVIEW MODAL (per-invoice remarks)
     ═══════════════════════════════════════════ */
  const renderBulkModal = () => {
    if (!bulkMode) return null;
    const isReject = bulkMode === 'rejected';
    const accent = isReject ? '#cf1322' : '#237804';
    const doneCount = Object.values(bulkResults).filter(r => r.ok).length;
    const failCount = Object.values(bulkResults).filter(r => !r.ok).length;
    const actionCount = bulkActionRows.length;

    const bulkCols = [
      { title: '#', key: 'n', width: 34, align: 'center', render: (_, __, i) => <span style={{ fontSize: 11, color: '#888' }}>{i + 1}</span> },
      { title: 'Invoice', key: 'inv', width: 230, render: (_, r) => (
        <div style={{ lineHeight: 1.35 }}>
          <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', color: '#000' }}>{r.vendor_name || '—'}</div>
          <div style={{ fontSize: 11, color: '#555' }}>
            Inv <b style={{ color: '#000' }}>{r.invoice_number || '—'}</b> · LPO {r.lpo_number || '—'}{r.batch_no ? ` · Batch ${r.batch_no}` : ''}
          </div>
          {r.status === 'on_hold' && <div style={{ fontSize: 10, color: HOLD_COLOR, fontWeight: 600 }}>On hold — will be released</div>}
          {isDecided(r) && <div style={{ fontSize: 10, color: '#cf1322', fontWeight: 600 }}>Currently {STATUS_CFG[r.status]?.label} → will be {isReject ? 'Rejected' : 'Approved'}</div>}
        </div>
      )},
      { title: 'Amount', dataIndex: 'total_amount', key: 'amt', width: 105, align: 'right',
        render: v => <span style={{ fontWeight: 700, fontSize: 12 }}>{fmtNum(v)}</span> },
      { title: isReject ? <span>Rejection Remark <span style={{ color: '#cf1322' }}>*</span></span> : 'Remark (optional)', key: 'rm', render: (_, r) => {
        const val = bulkRemarks[r.id] || '';
        const missing = isReject && !val.trim();
        return (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={val}
            onChange={e => setRowRemark(r.id, e.target.value)}
            status={missing ? 'error' : undefined}
            disabled={bulkRunning || bulkResults[r.id]?.ok}
            placeholder={isReject ? 'Why is this invoice rejected? (uploader sees this)' : (bulkCommon.trim() ? `Uses common: ${bulkCommon.trim()}` : 'Optional remark')}
            style={{ borderRadius: 3, fontSize: 12 }}
          />
        );
      }},
      { title: '', key: 'res', width: 70, align: 'center', render: (_, r) => {
        const res = bulkResults[r.id];
        if (res?.ok) return <Tag color="success" style={{ margin: 0, fontSize: 10 }}>Done</Tag>;
        if (res && !res.ok) return <Tooltip title={res.error}><Tag color="error" style={{ margin: 0, fontSize: 10, cursor: 'help' }}>Failed</Tag></Tooltip>;
        if (bulkRunning) return <LoadingOutlined spin style={{ color: '#888' }} />;
        return (
          <Tooltip title="Remove from this batch of actions">
            <Button type="text" size="small" icon={<CloseOutlined style={{ fontSize: 11, color: '#999' }} />}
              onClick={() => { removeFromSelection(r.id); if (bulkActionRows.length <= 1) closeBulk(); }} />
          </Tooltip>
        );
      }},
    ];

    return (
      <Modal
        open
        width={980}
        onCancel={closeBulk}
        maskClosable={false}
        closable={!bulkRunning}
        destroyOnClose
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_FAMILY }}>
            {isReject ? <CloseCircleOutlined style={{ color: accent }} /> : <CheckCircleOutlined style={{ color: accent }} />}
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {isReject ? 'Bulk Reject' : 'Bulk Approve'} — {actionCount} invoice{actionCount !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: 12, color: '#888', fontWeight: 500 }}>· {fmt(bulkActionTotal)}</span>
          </div>
        }
        footer={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_FAMILY }}>
            <span style={{ fontSize: 11, color: '#888', marginRight: 'auto', textAlign: 'left' }}>
              {bulkRunning
                ? `Processing… ${doneCount + failCount} of ${actionCount}`
                : isReject
                  ? (bulkMissingCount > 0
                      ? <span style={{ color: '#cf1322' }}>{bulkMissingCount} invoice{bulkMissingCount > 1 ? 's' : ''} still need{bulkMissingCount === 1 ? 's' : ''} a remark</span>
                      : <span style={{ color: '#237804' }}>All invoices have a rejection remark</span>)
                  : 'Empty remarks use the common remark (if any)'}
            </span>
            <Button onClick={closeBulk} disabled={bulkRunning} style={S.plainBtn}>Cancel</Button>
            <Button
              type="primary"
              danger={isReject}
              loading={bulkRunning}
              disabled={!actionCount || (isReject && bulkMissingCount > 0)}
              onClick={runBulk}
              style={{ ...S.plainBtn, fontWeight: 600, ...(isReject ? {} : { background: '#237804', borderColor: '#237804' }) }}>
              {failCount > 0 && !bulkRunning ? 'Retry Failed' : (isReject ? `Reject ${actionCount}` : `Approve ${actionCount}`)}
            </Button>
          </div>
        }
      >
        <div style={{ fontFamily: FONT_FAMILY }}>
          {isReject && (
            <Alert type="error" showIcon={false}
              message={<span style={{ fontSize: 12 }}>Each invoice needs its own rejection remark — the uploader sees the remark written against their invoice. Use the common remark below to fill quickly, then edit any row.</span>}
              style={{ borderRadius: 3, marginBottom: 10, padding: '6px 10px' }} />
          )}

          <div style={{ border: '1px solid #e8e8e8', background: '#fafafa', borderRadius: 3, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ ...S.subLabel, marginBottom: 5 }}>Common remark</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextArea
                autoSize={{ minRows: 1, maxRows: 3 }}
                value={bulkCommon}
                onChange={e => setBulkCommon(e.target.value)}
                disabled={bulkRunning}
                placeholder={isReject ? 'e.g. TRN on invoice does not match vendor master' : 'Optional — applied to rows left empty'}
                style={{ borderRadius: 3, fontSize: 12, flex: '1 1 320px' }}
              />
              <Button size="small" onClick={() => applyCommonRemark(true)} disabled={bulkRunning} style={S.plainBtn}>Fill empty rows</Button>
              <Button size="small" onClick={() => applyCommonRemark(false)} disabled={bulkRunning} style={S.plainBtn}>Overwrite all</Button>
            </div>
            {isReject && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
                {QUICK_REJECT_REASONS.map(q => (
                  <Tag key={q} onClick={() => !bulkRunning && setBulkCommon(q)}
                    style={{ cursor: bulkRunning ? 'default' : 'pointer', borderRadius: 3, fontSize: 10, margin: 0, background: '#fff', borderColor: '#d9d9d9' }}>
                    {q}
                  </Tag>
                ))}
              </div>
            )}
          </div>

          {bulkSkipRows.length > 0 && (
            <Alert type="info" showIcon={false}
              message={<span style={{ fontSize: 12 }}>
                {bulkSkipRows.length} selected invoice{bulkSkipRows.length > 1 ? 's are' : ' is'} already {isReject ? 'Rejected' : 'Approved'} and will be skipped:{' '}
                <b>{bulkSkipRows.map(r => r.invoice_number || r.submission_ref).join(', ')}</b>
              </span>}
              style={{ borderRadius: 3, marginBottom: 10, padding: '6px 10px' }} />
          )}

          <Table
            columns={bulkCols}
            dataSource={bulkActionRows.map(r => ({ ...r, key: r.id }))}
            pagination={false}
            size="small"
            bordered
            scroll={{ y: 420 }}
            className="pts-table pts-plain"
          />
        </div>
      </Modal>
    );
  };

  const globalSearchColumns = [
    { title: 'Batch', dataIndex: 'batch_no', key: 'b', width: 70, render: v => v ? <Tag style={{ borderRadius: 3, fontWeight: 700, fontSize: 11, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>{v}</Tag> : '-' },
    { title: 'Ref', dataIndex: 'submission_ref', key: 'r', width: 130, render: (t,r) => <a onClick={() => openDetail(r.id)} style={{ fontWeight: 600, fontSize: 12, color: '#1a5276', cursor: 'pointer' }}>{t}</a> },
    { title: 'Category', dataIndex: 'category', key: 'c', width: 120, render: c => <span style={{ fontSize: 11 }}>{CATEGORY_LABELS[c]||c}</span> },
    { title: 'LPO', dataIndex: 'lpo_number', key: 'l', width: 80, render: t => <span style={{ fontSize: 12, fontWeight: 600 }}>{t}</span> },
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'v', ellipsis: true, width: 180, render: t => <span style={{ fontSize: 12 }}>{t||'-'}</span> },
    { title: 'Invoice', dataIndex: 'invoice_number', key: 'i', width: 120, render: t => t ? <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #91caff' }}>{t}</Tag> : '-' },
    { title: 'Total', dataIndex: 'total_amount', key: 'a', width: 120, align: 'right', render: v => <span style={{ fontSize: 12, fontWeight: 600 }}>{fmt(v)}</span> },
    { title: 'Status', dataIndex: 'status', key: 's', width: 110, render: s => statusTag(s) },
    { title: 'Date', dataIndex: 'submitted_at', key: 'd', width: 90, render: v => <span style={{ fontSize: 11, color: '#888' }}>{fmtDate(v)}</span> },
    { title: '', key: 'x', width: 70, render: (_,r) => (<Space size={2}>
      <Button type="text" size="small" icon={<EyeOutlined style={{ color: '#1a5276' }} />} onClick={() => openDetail(r.id)} />
      <Tooltip title={rootDirHandle ? `Save to ${rootDirName}/Batch-${r.batch_no}/` : 'Save PDF'}><Button type="text" size="small" icon={<SaveOutlined style={{ color: '#237804' }} />} onClick={() => handleSaveSubmissionPdf(r.id, r.batch_no)} /></Tooltip>
    </Space>) },
  ];

  /* ═══════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════ */
  return (<div style={S.page}>
    <div style={{ ...S.panel, padding: '12px 18px' }}>
      <h2 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>Procurement Invoice Review</h2>
      <div style={{ fontSize: 11, color: '#888' }}>Approve, reject or hold invoices — tick several to bulk approve / reject (or flip Approved ⇄ Rejected) — export any status to Excel — link a folder to auto-save PDFs into batch subfolders</div>
    </div>

    <div style={{ ...S.panel, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SearchOutlined style={{ color: '#1a5276', fontSize: 14 }} />
        <Input size="small" placeholder="Search across all batches — LPO, vendor, invoice, ref..." value={globalQuery} onChange={e => setGlobalQuery(e.target.value)} allowClear suffix={globalSearching ? <Spin size="small" /> : null} style={{ borderRadius: 3, flex: 1, fontSize: 12 }} />
      </div>
    </div>

    {globalSearched && <div style={S.panel}>
      <div style={S.panelHeader}>
        <SearchOutlined style={{ color: '#1a5276', fontSize: 12 }} />
        <span style={S.sectionTitle}>Search Results</span>
        <Tag style={{ borderRadius: 3, fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '18px', color: '#1a5276', background: '#e8f4fd', border: '1px solid #7fb3d3' }}>{globalResults.length} found</Tag>
        <Button type="link" size="small" onClick={() => { setGlobalQuery(''); setGlobalResults([]); setGlobalSearched(false); }} style={{ marginLeft: 'auto', fontSize: 11, padding: 0 }}>Clear</Button>
      </div>
      {globalSearching ? <div style={{ padding: 25, textAlign: 'center' }}><Spin /></div> :
        globalResults.length === 0 ? <Empty style={{ padding: 18 }} image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No results for "${globalQuery}"`} /> :
        <Table rowSelection={rowSelection} columns={globalSearchColumns} dataSource={globalResults.map(s => ({ ...s, key: s.id }))} rowClassName={rowClass} pagination={globalResults.length > 15 ? { pageSize: 15, size: 'small' } : false} size="small" bordered scroll={{ x: 1114 }} className="pts-table" />
      }
    </div>}

    <Tabs
      activeKey={activeMainTab}
      onChange={key => {
        setActiveMainTab(key);
        setAllPage(1);
        clearSelection();
        if (key === 'pending') { setAllStatusFilter('pending_review'); setAllCategoryFilter('all'); }
        else if (key === 'hold') { setAllStatusFilter('on_hold'); setAllCategoryFilter('all'); }
        else if (key === 'all') { setAllStatusFilter('all'); setAllCategoryFilter('all'); }
      }}
      items={[
        { key: 'batches', label: <span style={{ fontSize: 12 }}>Batch Management</span>, children: renderBatchesTab() },
        { key: 'all',     label: <span style={{ fontSize: 12 }}>All Submissions <span style={{ color: '#888', fontSize: 10 }}>({stats?.total || 0})</span></span>, children: renderAllTab() },
        { key: 'pending', label: <span style={{ fontSize: 12 }}>Pending Review {receivedStats.pending > 0 && <Badge count={receivedStats.pending} size="small" style={{ marginLeft: 4 }} />}</span>, children: renderAllTab() },
        { key: 'hold',    label: <span style={{ fontSize: 12 }}>On Hold {(stats?.on_hold > 0) && <Badge count={stats.on_hold} size="small" style={{ marginLeft: 4, background: HOLD_COLOR }} />}</span>, children: renderAllTab() },
      ]}
    />

    {renderBatchDrawer()}
    {detailLoading && detailId && <Drawer open onClose={closeDetail} placement="right" width={700} styles={{ body: { display: 'flex', justifyContent: 'center', alignItems: 'center' } }}><Spin /></Drawer>}
    {detailData && renderDetailDrawer()}
    {renderDocPreview()}
    {renderBulkBar()}
    {renderBulkModal()}

    <style>{`
      .pts-table .ant-table-bordered .ant-table-thead > tr > th {
        background: #f5f5f5 !important;
        font-weight: 600 !important;
        font-size: 11px !important;
        color: #333 !important;
        border-bottom: 2px solid #d9d9d9 !important;
        padding: 6px 8px !important;
        font-family: ${FONT_FAMILY} !important;
      }
      .pts-table .ant-table-bordered .ant-table-tbody > tr > td {
        padding: 5px 6px !important;
        font-size: 12px !important;
        font-family: ${FONT_FAMILY} !important;
      }
      .pts-table .ant-table-bordered .ant-table-tbody > tr:hover > td { background: #fafafa !important; }
      .pts-table .ant-table-cell-row-hover { background: #fafafa !important; }

      /* ── PLAIN (monochrome) tables ── */
      .pts-plain .ant-table-thead > tr > th { color: #000 !important; font-weight: 700 !important; background: #fff !important; border-bottom: 2px solid #000 !important; padding: 5px 7px !important; }
      .pts-plain .ant-table-tbody > tr > td { color: #000 !important; padding: 4px 7px !important; }
      .pts-plain .ant-table-thead > tr > th.pts-col-yellow { background: #fffbe6 !important; }
      .pts-plain td.pts-col-yellow { background: #fffbe6 !important; }
      .pts-plain .ant-table-tbody > tr:hover > td.pts-col-yellow { background: #fff3bf !important; }

      /* ── ON HOLD rows — faint amber, left marker ── */
      .pts-table .pts-row-hold > td { background: #fff7e6 !important; }
      .pts-table .pts-row-hold:hover > td { background: #ffefd6 !important; }
      .pts-plain .pts-row-hold > td.pts-col-yellow { background: #fff3d6 !important; }
      .pts-table .pts-row-hold > td:first-child { box-shadow: inset 3px 0 0 ${HOLD_COLOR}; }

      /* ── v16 SELECTED rows — faint blue wins over yellow / amber ── */
      .pts-table .pts-row-selected > td,
      .pts-plain .pts-row-selected > td.pts-col-yellow { background: #e6f4ff !important; }
      .pts-table .pts-row-selected:hover > td { background: #d6ebff !important; }
      .pts-table .ant-table-selection-column { padding-left: 6px !important; padding-right: 6px !important; }

      .ant-btn { box-shadow: none !important; font-family: ${FONT_FAMILY} !important; }
      .ant-tabs-tab { padding: 6px 14px !important; font-family: ${FONT_FAMILY} !important; }
      .ant-tabs-tab-active .ant-tabs-tab-btn { color: #1a5276 !important; font-weight: 600; }
      .ant-tabs-ink-bar { background: #1a5276 !important; }
    `}</style>
  </div>);
};

export default ProcurementAccountsView;